/**
 * PatientEducationFinder Class
 * 
 * Handles zipcode-based Patient Education Manager lookup functionality
 * Supports multiple instances on the same page with proper state management
 */
class PatientEducationFinder {
  constructor(container, options = {}) {
    // Validate container element
    if (!container || !(container instanceof Element)) {
      throw new Error('PatientEducationFinder requires a valid DOM element as container')
    }

    this.container = container
    
    // Default configuration options
    this.options = {
      dataUrl: '/sites/default/themes/custom/tryngolza/files/patient-education-managers-by-state.json',
      enrollmentUrl: 'https://ioniseverystep.iassist.com/pt/ole/available-services',
      loadingText: 'Searching...',
      notFoundText: 'No Patient Education Manager found for this zip code.',
      errorText: 'An error occurred while searching. Please try again.',
      validateOnInput: true,
      autoInit: true,
      // Network retry configuration
      maxRetries: 3,
      retryDelay: 1000, // Initial delay in milliseconds
      maxRetryDelay: 8000, // Maximum delay in milliseconds
      retryMultiplier: 2, // Exponential backoff multiplier
      timeoutMs: 10000, // Request timeout in milliseconds
      // Fallback contact configuration
      fallbackContact: {
        phone: '1-800-TRYNGOLZA',
        phoneDisplay: '1-800-TRYNGOLZA (1-800-879-6465)',
        email: 'support@tryngolza.com',
        hours: 'Monday - Friday, 8 AM - 8 PM ET',
        website: 'https://www.tryngolza.com/support',
        alternativeText: 'For assistance finding a Patient Education Manager in your area'
      },
      ...options
    }

    // Instance state management
    this.state = {
      isInitialized: false,
      isLoading: false,
      data: null,
      currentZipcode: '',
      lastSearchResult: null,
      debounceTimer: null,
      specificZipMap: null, // Map for O(1) specific zipcode lookups
      // Network retry state
      retryCount: 0,
      isRetrying: false,
      lastError: null,
      isOnline: navigator.onLine
    }

    // DOM element references (will be set during init)
    this.elements = {
      form: null,
      input: null,
      submitButton: null,
      resultsContainer: null,
      errorContainer: null
    }

    // Bound methods for event listeners (for proper cleanup)
    this.boundHandlers = {
      handleSubmit: this.handleSubmit.bind(this),
      handleInput: this.handleInput.bind(this),
      handleInputChange: this.handleInputChange.bind(this),
      handlePaste: this.handlePaste.bind(this),
      handleKeyDown: this.handleKeyDown.bind(this),
      handleKeyUp: this.handleKeyUp.bind(this),
      handleFocus: this.handleFocus.bind(this),
      handleBlur: this.handleBlur.bind(this)
    }

    // Auto-initialize if enabled
    if (this.options.autoInit) {
      this.init()
    }
  }

  /**
   * Initialize the component
   * Sets up DOM elements, loads data, and binds event listeners
   */
  async init() {
    if (this.state.isInitialized) {
      console.warn('PatientEducationFinder already initialized')
      return this
    }

    try {
      // Initialize error logging first
      this.initializeErrorLogging()
      
      this.setupDOMElements()
      this.bindEvents()
      this.setupNetworkListeners()
      this.clearErrorOnUserAction()
      
      // Load data asynchronously
      const loadStartTime = performance.now()
      await this.loadData()
      const loadEndTime = performance.now()
      
      // Log performance metrics
      this.logPerformance('dataLoadTime', loadEndTime - loadStartTime)
      this.logging.performanceMetrics.dataLoadTime = loadEndTime - loadStartTime
      
      this.state.isInitialized = true
      
      this.logInfo('PatientEducationFinder initialized successfully', {
        initTime: loadEndTime - this.logging.performanceMetrics.initTime,
        dataLoadTime: loadEndTime - loadStartTime
      })
      
      console.log('PatientEducationFinder initialized successfully')
    } catch (error) {
      this.logError('Failed to initialize PatientEducationFinder', {
        error: error.message,
        stack: error.stack
      }, error)
      console.error('Failed to initialize PatientEducationFinder:', error)
      this.showError('Failed to initialize the Patient Education Manager finder.')
    }

    return this
  }

  /**
   * Set up DOM element references
   * @private
   */
  setupDOMElements() {
    // Find form elements within the container
    this.elements.form = this.container.querySelector('form')
    this.elements.input = this.container.querySelector('input[type="text"], input[type="search"]')
    this.elements.submitButton = this.container.querySelector('button[type="submit"], input[type="submit"]')
    
    // Create or find results and error containers
    this.elements.resultsContainer = this.container.querySelector('.results-container') || 
      this.createResultsContainer()
    this.elements.errorContainer = this.container.querySelector('.error-container') || 
      this.createErrorContainer()
    this.elements.validationMessageContainer = this.container.querySelector('.validation-message-container') ||
      this.createValidationMessageContainer()

    // Validate required elements
    if (!this.elements.form || !this.elements.input) {
      throw new Error('Required form elements not found in container')
    }

    // Set up accessibility attributes
    this.setupAccessibilityAttributes()
  }

  /**
   * Create results container if it doesn't exist
   * @private
   */
  createResultsContainer() {
    const container = document.createElement('div')
    container.className = 'results-container'
    container.setAttribute('aria-live', 'polite')
    container.setAttribute('aria-label', 'Search results')
    this.container.appendChild(container)
    return container
  }

  /**
   * Create error container if it doesn't exist
   * @private
   */
  createErrorContainer() {
    const container = document.createElement('div')
    container.className = 'error-container'
    container.setAttribute('role', 'alert')
    container.setAttribute('aria-live', 'assertive')
    this.container.appendChild(container)
    return container
  }

  /**
   * Create validation message container if it doesn't exist
   * @private
   */
  createValidationMessageContainer() {
    const container = document.createElement('div')
    container.className = 'validation-message-container'
    container.setAttribute('aria-live', 'polite')
    container.setAttribute('aria-atomic', 'true')
    container.style.display = 'none'
    this.container.appendChild(container)
    return container
  }

  /**
   * Set up accessibility attributes for form elements
   * @private
   */
  setupAccessibilityAttributes() {
    if (!this.elements.input) return

    // Generate unique IDs for accessibility
    const inputId = this.elements.input.id || `zipcode-input-${Date.now()}`
    const errorId = `${inputId}-error`
    const validationId = `${inputId}-validation`
    const helpId = `${inputId}-help`

    // Set input attributes
    this.elements.input.id = inputId
    this.elements.input.setAttribute('aria-describedby', `${errorId} ${validationId} ${helpId}`)
    this.elements.input.setAttribute('aria-invalid', 'false')
    this.elements.input.setAttribute('inputmode', 'numeric')
    this.elements.input.setAttribute('pattern', '[0-9]{5}')
    this.elements.input.setAttribute('maxlength', '5')
    this.elements.input.setAttribute('autocomplete', 'postal-code')
    this.elements.input.setAttribute('aria-label', 'Enter your 5-digit zip code to find a Patient Education Manager')

    // Set container IDs for aria-describedby
    this.elements.errorContainer.id = errorId
    this.elements.validationMessageContainer.id = validationId

    // Add help text container if it doesn't exist
    if (!this.container.querySelector(`#${helpId}`)) {
      const helpContainer = document.createElement('div')
      helpContainer.id = helpId
      helpContainer.className = 'zipcode-help-text sr-only'
      helpContainer.textContent = 'Enter a 5-digit US zipcode. Example: 12345'
      this.container.appendChild(helpContainer)
    }

    // Add label if it doesn't exist
    if (!this.container.querySelector(`label[for="${inputId}"]`)) {
      const existingLabel = this.container.querySelector('label')
      if (existingLabel) {
        existingLabel.setAttribute('for', inputId)
      }
    }
  }

  /**
   * Bind event listeners
   * @private
   */
  bindEvents() {
    if (this.elements.form) {
      this.elements.form.addEventListener('submit', this.boundHandlers.handleSubmit)
    }

    if (this.elements.input) {
      if (this.options.validateOnInput) {
        this.elements.input.addEventListener('input', this.boundHandlers.handleInput)
      }
      this.elements.input.addEventListener('change', this.boundHandlers.handleInputChange)
      this.elements.input.addEventListener('paste', this.boundHandlers.handlePaste)
      this.elements.input.addEventListener('keydown', this.boundHandlers.handleKeyDown)
      this.elements.input.addEventListener('keyup', this.boundHandlers.handleKeyUp)
      this.elements.input.addEventListener('focus', this.boundHandlers.handleFocus)
      this.elements.input.addEventListener('blur', this.boundHandlers.handleBlur)
    }

    if (this.elements.submitButton) {
      this.elements.submitButton.addEventListener('keydown', this.boundHandlers.handleKeyDown)
      this.elements.submitButton.addEventListener('focus', this.boundHandlers.handleFocus)
      this.elements.submitButton.addEventListener('blur', this.boundHandlers.handleBlur)
    }

    // Add global keyboard event listener for escape key
    document.addEventListener('keydown', this.boundHandlers.handleKeyDown)
  }

  /**
   * Set up network event listeners for online/offline detection
   * @private
   */
  setupNetworkListeners() {
    // Bind network event handlers
    this.boundHandlers.handleOnline = this.handleOnline.bind(this)
    this.boundHandlers.handleOffline = this.handleOffline.bind(this)

    // Listen for network status changes
    window.addEventListener('online', this.boundHandlers.handleOnline)
    window.addEventListener('offline', this.boundHandlers.handleOffline)
  }

  /**
   * Handle online event - network connection restored
   * @private
   */
  handleOnline() {
    this.state.isOnline = true
    console.log('Network connection restored')
    
    // If we had a network error and data isn't loaded, attempt to reload
    if (!this.state.data && this.state.lastError && this.state.lastError.type === 'network') {
      console.log('Attempting to reload data after network restoration')
      this.loadData().catch(error => {
        console.error('Failed to reload data after network restoration:', error)
      })
    }
  }

  /**
   * Handle offline event - network connection lost
   * @private
   */
  handleOffline() {
    this.state.isOnline = false
    console.log('Network connection lost')
  }

  /**
   * Handle form submission
   * @private
   */
  async handleSubmit(event) {
    event.preventDefault()
    
    const zipcode = this.elements.input.value.trim()
    
    // Log user action
    this.logUserAction('form_submit', {
      zipcode: zipcode,
      hasData: !!this.state.data,
      isInitialized: this.state.isInitialized
    })
    
    if (this.validateZipcode(zipcode)) {
      const searchStartTime = performance.now()
      const searchResponse = await this.searchManager(zipcode)
      const searchEndTime = performance.now()
      
      // Log search performance
      this.logPerformance('searchTime', searchEndTime - searchStartTime, {
        zipcode: zipcode,
        success: searchResponse.success
      })
      
      // Handle search response for additional logging or future features
      if (searchResponse.success) {
        this.logInfo('Manager found successfully', {
          managerName: searchResponse.manager.name,
          zipcode: zipcode
        })
        console.log('Manager found successfully:', searchResponse.manager.name)
      } else {
        this.logWarning('Search failed', {
          error: searchResponse.error,
          zipcode: zipcode,
          hasFallbackContact: !!searchResponse.fallbackContact
        })
        console.log('Search failed:', searchResponse.error)
        if (searchResponse.fallbackContact) {
          console.log('Fallback contact available:', searchResponse.fallbackContact)
        }
      }
    }
  }

  /**
   * Handle input events (real-time validation and formatting)
   * @private
   */
  handleInput(event) {
    // Format the input value first
    const formattedValue = this.formatZipcodeInput(event.target.value)
    
    // Update the input value if it was changed by formatting
    if (event.target.value !== formattedValue) {
      event.target.value = formattedValue
    }
    
    const zipcode = formattedValue.trim()
    this.state.currentZipcode = zipcode
    
    // Clear previous errors on input
    this.clearError()
    
    // Provide immediate feedback for obviously invalid input
    if (zipcode.length > 0) {
      // Check for immediate validation issues (non-numeric, too long)
      if (zipcode.length > 5 || /\D/.test(zipcode)) {
        const isValid = this.validateZipcode(zipcode, true) // Show errors immediately for obvious issues
        this.updateValidationUI(isValid, zipcode)
        return
      }
      
      // Use debounced validation for potentially valid input
      this.debouncedValidation(zipcode)
    } else {
      // Clear validation UI for empty input
      this.updateValidationUI(false, '')
    }
  }

  /**
   * Handle input change events
   * @private
   */
  handleInputChange(event) {
    const zipcode = event.target.value.trim()
    this.state.currentZipcode = zipcode
  }

  /**
   * Handle paste events to format pasted content
   * @private
   */
  handlePaste(event) {
    // Prevent default paste behavior
    event.preventDefault()
    
    // Get pasted text from clipboard
    const pastedText = (event.clipboardData || window.clipboardData).getData('text')
    
    // Format the pasted text
    const formattedText = this.formatZipcodeInput(pastedText)
    
    // Set the formatted text as the input value
    event.target.value = formattedText
    
    // Trigger input event to validate the pasted content
    const inputEvent = new Event('input', { bubbles: true })
    event.target.dispatchEvent(inputEvent)
  }

  /**
   * Handle keyboard events for enhanced navigation and accessibility
   * @private
   */
  handleKeyDown(event) {
    // Only handle events for elements within this component or global escape
    if (!this.container.contains(event.target) && event.key !== 'Escape') {
      return
    }

    const { key, target, ctrlKey, metaKey, shiftKey } = event
    
    // Log keyboard interaction
    this.logUserAction('keyboard_interaction', {
      key: key,
      element: target.tagName,
      elementId: target.id,
      ctrlKey: ctrlKey,
      metaKey: metaKey,
      shiftKey: shiftKey
    })

    switch (key) {
    case 'Escape':
      this.handleEscapeKey(event)
      break
      
    case 'Enter':
      this.handleEnterKey(event)
      break
      
    case ' ':
    case 'Space':
      this.handleSpaceKey(event)
      break
      
    case 'Tab':
      this.handleTabKey(event)
      break
      
    case 'ArrowDown':
    case 'ArrowUp':
      this.handleArrowKeys(event)
      break
      
    case 'Home':
    case 'End':
      this.handleHomeEndKeys(event)
      break
      
    default:
      // Handle other keys if needed
      break
    }
  }

  /**
   * Handle keyup events for specific functionality
   * @private
   */
  handleKeyUp(_event) {
    // Currently no specific keyup handling needed
    // This method is available for future enhancements
  }

  /**
   * Handle focus events for accessibility and user experience
   * @private
   */
  handleFocus(event) {
    const element = event.target
    
    // Log focus event
    this.logUserAction('element_focus', {
      element: element.tagName,
      elementId: element.id,
      elementClass: element.className
    })

    // Add focus class for enhanced styling
    element.classList.add('focused')
    
    // Clear any previous errors when user focuses on input
    if (element === this.elements.input) {
      this.clearError()
      this.clearInlineValidationError()
    }

    // Announce focus to screen readers if needed
    this.announceFocusChange(element)
  }

  /**
   * Handle blur events for cleanup and validation
   * @private
   */
  handleBlur(event) {
    const element = event.target
    
    // Remove focus class
    element.classList.remove('focused')
    
    // Validate input on blur if it has content
    if (element === this.elements.input && element.value.trim()) {
      this.validateZipcode(element.value.trim(), true)
    }
  }

  /**
   * Handle Escape key press
   * @private
   */
  handleEscapeKey(event) {
    // Clear errors and reset form state
    this.clearError()
    this.clearResults()
    this.clearInlineValidationError()
    
    // If input has focus, clear it
    if (this.elements.input && document.activeElement === this.elements.input) {
      this.elements.input.value = ''
      this.state.currentZipcode = ''
    }
    
    // Announce escape action to screen readers
    this.announceToScreenReader('Form reset. Please enter a new zip code.')
    
    // Prevent default escape behavior
    event.preventDefault()
  }

  /**
   * Handle Enter key press
   * @private
   */
  handleEnterKey(event) {
    const target = event.target
    
    // If Enter is pressed on input or submit button, submit the form
    if (target === this.elements.input || target === this.elements.submitButton) {
      event.preventDefault()
      this.elements.form.dispatchEvent(new Event('submit', { bubbles: true }))
    }
  }

  /**
   * Handle Space key press
   * @private
   */
  handleSpaceKey(event) {
    const target = event.target
    
    // Space key should only activate buttons, not inputs
    if (target === this.elements.submitButton) {
      event.preventDefault()
      this.elements.form.dispatchEvent(new Event('submit', { bubbles: true }))
    }
  }

  /**
   * Handle Tab key for focus management
   * @private
   */
  handleTabKey(event) {
    // Let browser handle tab navigation naturally
    // This method is available for custom tab behavior if needed
    
    // Log tab navigation for analytics
    this.logUserAction('tab_navigation', {
      direction: event.shiftKey ? 'backward' : 'forward',
      fromElement: event.target.tagName,
      fromElementId: event.target.id
    })
  }

  /**
   * Handle Arrow keys for potential future navigation
   * @private
   */
  handleArrowKeys(event) {
    // Currently no specific arrow key behavior
    // This method is available for future enhancements like result navigation
    
    // For input fields, let browser handle cursor movement naturally
    if (event.target === this.elements.input) {
      return
    }
  }

  /**
   * Handle Home and End keys
   * @private
   */
  handleHomeEndKeys(event) {
    // For input fields, let browser handle cursor movement naturally
    if (event.target === this.elements.input) {
      return
    }
  }

  /**
   * Announce focus changes to screen readers
   * @private
   */
  announceFocusChange(element) {
    let announcement = ''
    
    if (element === this.elements.input) {
      announcement = 'Zip code input field focused. Enter a 5-digit zip code.'
    } else if (element === this.elements.submitButton) {
      announcement = 'Search button focused. Press Enter or Space to search.'
    }
    
    if (announcement) {
      this.announceToScreenReader(announcement)
    }
  }

  /**
   * Announce messages to screen readers
   * @private
   */
  announceToScreenReader(message) {
    // Find or create announcement container
    let announcer = this.container.querySelector('#announcements')
    
    if (!announcer) {
      announcer = document.createElement('div')
      announcer.id = 'announcements'
      announcer.className = 'sr-only'
      announcer.setAttribute('aria-live', 'assertive')
      announcer.setAttribute('aria-atomic', 'true')
      this.container.appendChild(announcer)
    }
    
    // Clear previous announcement and set new one
    announcer.textContent = ''
    setTimeout(() => {
      announcer.textContent = message
    }, 100)
    
    // Clear announcement after a delay
    setTimeout(() => {
      announcer.textContent = ''
    }, 3000)
  }

  /**
   * Format zipcode input by removing non-numeric characters and limiting to 5 digits
   * @param {string} input - The raw input value
   * @returns {string} Formatted zipcode string (numeric only, max 5 digits)
   */
  formatZipcodeInput(input) {
    if (typeof input !== 'string') {
      return ''
    }
    
    // Remove all non-numeric characters
    const numericOnly = input.replace(/\D/g, '')
    
    // Limit to 5 digits maximum
    return numericOnly.slice(0, 5)
  }

  /**
   * Debounced validation to avoid excessive validation calls
   * @param {string} zipcode - The zipcode to validate after debounce delay
   * @private
   */
  debouncedValidation(zipcode) {
    // Clear any existing timer
    if (this.state.debounceTimer) {
      clearTimeout(this.state.debounceTimer)
    }
    
    // Set new timer for 300ms delay
    this.state.debounceTimer = setTimeout(() => {
      // Only validate if the zipcode hasn't changed
      if (this.state.currentZipcode === zipcode) {
        const isValid = this.validateZipcode(zipcode, false) // Don't show errors for incomplete input
        this.updateValidationUI(isValid, zipcode)
      }
    }, 300)
  }

  /**
   * Update visual feedback based on validation state
   * @param {boolean} isValid - Whether the current input is valid
   * @param {string} zipcode - The current zipcode value
   * @private
   */
  updateValidationUI(isValid, zipcode) {
    if (!this.elements.input) return

    // Remove all validation classes first
    this.elements.input.classList.remove('valid', 'invalid', 'incomplete')
    this.container.classList.remove('input-valid', 'input-invalid', 'input-incomplete')

    // Clear previous validation messages
    this.clearValidationMessage()
    this.clearInlineValidationError()

    // Don't apply styling for empty input
    if (!zipcode || zipcode.trim() === '') {
      this.elements.input.setAttribute('aria-invalid', 'false')
      return
    }

    // Apply appropriate classes and ARIA attributes based on validation state
    if (zipcode.length === 5 && isValid) {
      // Complete and valid zipcode
      this.elements.input.classList.add('valid')
      this.container.classList.add('input-valid')
      this.elements.input.setAttribute('aria-invalid', 'false')
      this.showInlineValidationSuccess('Valid Zip code format')
    } else if (zipcode.length < 5 && /^\d+$/.test(zipcode)) {
      // Incomplete but potentially valid (all digits, less than 5)
      this.elements.input.classList.add('incomplete')
      this.container.classList.add('input-incomplete')
      this.elements.input.setAttribute('aria-invalid', 'false')
      
      const remaining = 5 - zipcode.length
      this.showValidationMessage(
        `Enter ${remaining} more digit${remaining === 1 ? '' : 's'} to complete your zip code`, 
        'info'
      )
    } else if (zipcode.length > 0) {
      // Invalid format - has content but not valid
      this.elements.input.classList.add('invalid')
      this.container.classList.add('input-invalid')
      this.elements.input.setAttribute('aria-invalid', 'true')
      
      // Provide specific guidance based on the type of error
      if (/[^0-9]/.test(zipcode)) {
        const invalidChars = zipcode.match(/[^0-9]/g).join(', ')
        this.showInlineValidationError(
          `Remove invalid characters: "${invalidChars}". Use numbers only (0-9).`
        )
      } else if (zipcode.length > 5) {
        this.showInlineValidationError(
          'Zipcode is too long. Please enter exactly 5 digits.'
        )
      } else {
        this.showInlineValidationError(
          'Please enter numbers only. Example: 12345'
        )
      }
    }
  }

  /**
   * Validate zipcode format (5-digit US zipcode)
   * @param {string} zipcode - The zipcode to validate
   * @param {boolean} showErrors - Whether to display validation errors to user
   * @returns {boolean} True if valid, false otherwise
   */
  validateZipcode(zipcode, showErrors = true) {
    // Handle null, undefined, or non-string inputs
    if (zipcode == null || typeof zipcode !== 'string') {
      if (showErrors) {
        this.showInlineValidationError('Please enter a valid 5-digit zip code. Example: 12345')
        this.setAriaInvalid(true)
      }
      return false
    }

    // Trim whitespace and check if empty
    const trimmedZipcode = zipcode.trim()
    if (trimmedZipcode === '') {
      if (showErrors) {
        this.showInlineValidationError('Zip code is required. Please enter your 5-digit zip code.')
        this.setAriaInvalid(true)
      }
      return false
    }

    // Validate 5-digit US zipcode format using regex
    const zipcodeRegex = /^\d{5}$/
    const isValid = zipcodeRegex.test(trimmedZipcode)

    if (!isValid && showErrors) {
      this.setAriaInvalid(true)
      
      // Provide specific, actionable error messages
      if (trimmedZipcode.length < 5) {
        const remaining = 5 - trimmedZipcode.length
        this.showInlineValidationError(
          `Please enter ${remaining} more digit${remaining === 1 ? '' : 's'}. Zip code must be exactly 5 digits. Example: 12345`
        )
      } else if (trimmedZipcode.length > 5) {
        this.showInlineValidationError(
          'Zipcode is too long. Please enter exactly 5 digits. Example: 12345'
        )
      } else if (/[^0-9]/.test(trimmedZipcode)) {
        // Contains non-numeric characters
        const invalidChars = trimmedZipcode.match(/[^0-9]/g).join(', ')
        this.showInlineValidationError(
          `Invalid characters detected: "${invalidChars}". Please use numbers only. Example: 12345`
        )
      } else {
        this.showInlineValidationError(
          'Invalid zip code format. Please enter exactly 5 digits. Example: 12345'
        )
      }
    } else if (isValid) {
      this.setAriaInvalid(false)
      this.clearInlineValidationError()
    }

    return isValid
  }

  /**
   * Set aria-invalid attribute on input element
   * @param {boolean} isInvalid - Whether the input is invalid
   * @private
   */
  setAriaInvalid(isInvalid) {
    if (this.elements.input) {
      this.elements.input.setAttribute('aria-invalid', isInvalid.toString())
    }
  }

  /**
   * Show inline validation error message near the input field
   * @param {string} message - The validation error message to display
   * @private
   */
  showInlineValidationError(message) {
    if (!this.elements.validationMessageContainer) return

    // Create inline error message with proper accessibility attributes
    this.elements.validationMessageContainer.innerHTML = `
      <div class="inline-validation-error" role="alert" aria-live="polite">
        <svg class="validation-error-icon" aria-hidden="true" viewBox="0 0 24 24" fill="currentColor">
          <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
        </svg>
        <span class="validation-error-text">${this.escapeHtml(message)}</span>
      </div>
    `
    this.elements.validationMessageContainer.style.display = 'block'
    
    // Add error styling to input and container
    if (this.elements.input) {
      this.elements.input.classList.add('validation-error')
      this.elements.input.classList.remove('validation-success')
    }
    this.container.classList.add('has-validation-error')
    this.container.classList.remove('has-validation-success')
  }

  /**
   * Clear inline validation error message
   * @private
   */
  clearInlineValidationError() {
    if (this.elements.validationMessageContainer) {
      this.elements.validationMessageContainer.innerHTML = ''
      this.elements.validationMessageContainer.style.display = 'none'
    }
    
    // Remove error styling
    if (this.elements.input) {
      this.elements.input.classList.remove('validation-error')
    }
    this.container.classList.remove('has-validation-error')
  }

  /**
   * Show inline validation success message
   * @param {string} message - The validation success message to display
   * @private
   */
  showInlineValidationSuccess(message = 'Valid Zip code format') {
    if (!this.elements.validationMessageContainer) return

    this.elements.validationMessageContainer.innerHTML = `
      <div class="inline-validation-success" role="status" aria-live="polite">
        <svg class="validation-success-icon" aria-hidden="true" viewBox="0 0 24 24" fill="currentColor">
          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
        </svg>
        <span class="validation-success-text">${this.escapeHtml(message)}</span>
      </div>
    `
    this.elements.validationMessageContainer.style.display = 'block'
    
    // Add success styling
    if (this.elements.input) {
      this.elements.input.classList.add('validation-success')
      this.elements.input.classList.remove('validation-error')
    }
    this.container.classList.add('has-validation-success')
    this.container.classList.remove('has-validation-error')
  }

  /**
   * Load Patient Education Manager data from the configured URL
   * @returns {Promise} Promise that resolves when data is loaded
   */
  async loadData() {
    // Return early if data is already loaded
    if (this.state.data) {
      return Promise.resolve(this.state.data)
    }

    // Reset retry state for new load attempt
    this.state.retryCount = 0
    this.state.lastError = null

    return this.retryWithBackoff(async () => {
      return this.performDataLoad()
    })
  }

  /**
   * Perform the actual data loading with timeout and error handling
   * @private
   */
  async performDataLoad() {
    // Check if we're offline
    if (!this.state.isOnline) {
      const error = new Error('No internet connection available')
      error.type = 'offline'
      throw error
    }

    // Create AbortController for timeout handling
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), this.options.timeoutMs)

    try {
      this.setLoading(true)
      
      const response = await fetch(this.options.dataUrl, {
        signal: controller.signal,
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      })
      
      clearTimeout(timeoutId)
      
      if (!response.ok) {
        const error = new Error(`HTTP error! status: ${response.status}`)
        error.status = response.status
        error.type = response.status >= 500 ? 'server' : 'client'
        throw error
      }
      
      let data
      try {
        data = await response.json()
      } catch (jsonError) {
        // Enhanced JSON parsing error handling
        const error = new Error('Failed to parse JSON response: ' + jsonError.message)
        error.type = 'json_parse'
        error.originalError = jsonError
        this.logValidationError('JSON parsing failed', {
          jsonError: jsonError.message,
          responseStatus: response.status,
          responseHeaders: Object.fromEntries(response.headers.entries())
        })
        throw error
      }
      
      // Enhanced data structure and integrity validation
      const validationResult = this.validateDataIntegrity(data)
      
      if (!validationResult.success) {
        // Log detailed validation errors
        this.logValidationError('Data validation failed', {
          errors: validationResult.errors,
          warnings: validationResult.warnings,
          totalManagers: data?.managers?.length || 0,
          invalidManagers: validationResult.invalidManagers.length
        })
        
        const error = new Error('Invalid or corrupted data structure')
        error.type = 'data_validation'
        error.validationResult = validationResult
        throw error
      }
      
      // Handle graceful degradation for partial data corruption
      if (validationResult.invalidManagers.length > 0) {
        console.warn(`Data partially corrupted: ${validationResult.invalidManagers.length} invalid managers filtered out`)
        
        // Use only valid managers
        data = {
          ...data,
          managers: validationResult.validManagers,
          metadata: validationResult.metadata || data.metadata
        }
        
        // Add validation metadata
        data._validationInfo = {
          totalOriginalManagers: validationResult.validManagers.length + validationResult.invalidManagers.length,
          validManagers: validationResult.validManagers.length,
          invalidManagers: validationResult.invalidManagers.length,
          warnings: validationResult.warnings,
          timestamp: new Date().toISOString()
        }
      }
      
      // Store the validated/cleaned data in state
      this.state.data = data
      
      // Build the specific zipcode lookup map for O(1) performance
      this.buildSpecificZipMap()
      
      // Reset error state on success
      this.state.lastError = null
      this.state.retryCount = 0
      
      console.log('Patient Education Manager data loaded successfully:', data.managers.length, 'managers')
      
      return data
      
    } catch (error) {
      clearTimeout(timeoutId)
      
      // Categorize the error for better handling
      if (error.name === 'AbortError') {
        error.type = 'timeout'
        error.message = 'Request timed out'
      } else if (error.name === 'TypeError' && error.message.includes('fetch')) {
        error.type = 'network'
        error.message = 'Network connection failed'
      }
      
      // Store error for potential retry logic
      this.state.lastError = {
        type: error.type || 'unknown',
        message: error.message,
        status: error.status,
        timestamp: Date.now()
      }
      
      console.error('Data loading failed:', error)
      throw error
      
    } finally {
      this.setLoading(false)
    }
  }

  /**
   * Retry logic with exponential backoff
   * @param {Function} operation - The async operation to retry
   * @returns {Promise} The result of the operation
   * @private
   */
  async retryWithBackoff(operation) {
    let lastError

    for (let attempt = 0; attempt <= this.options.maxRetries; attempt++) {
      try {
        this.state.retryCount = attempt
        
        if (attempt > 0) {
          this.state.isRetrying = true
          console.log(`Retry attempt ${attempt}/${this.options.maxRetries}`)
        }
        
        const result = await operation()
        this.state.isRetrying = false
        return result
        
      } catch (error) {
        lastError = error
        
        // Don't retry for certain error types
        if (this.shouldNotRetry(error)) {
          break
        }
        
        // Don't retry if this is the last attempt
        if (attempt === this.options.maxRetries) {
          break
        }
        
        // Calculate delay with exponential backoff and jitter
        const baseDelay = this.options.retryDelay * Math.pow(this.options.retryMultiplier, attempt)
        const maxDelay = this.options.maxRetryDelay
        const delay = Math.min(baseDelay, maxDelay)
        
        // Add jitter (±25% of delay) to prevent thundering herd
        const jitter = delay * 0.25 * (Math.random() * 2 - 1)
        const finalDelay = Math.max(0, delay + jitter)
        
        console.log(`Retrying in ${Math.round(finalDelay)}ms...`)
        await this.sleep(finalDelay)
      }
    }
    
    this.state.isRetrying = false
    
    // Handle final error after all retries exhausted
    this.handleNetworkError(lastError)
    throw lastError
  }

  /**
   * Determine if an error should not be retried
   * @param {Error} error - The error to check
   * @returns {boolean} True if should not retry
   * @private
   */
  shouldNotRetry(error) {
    // Don't retry for client errors (4xx) except 408 (timeout) and 429 (rate limit)
    if (error.status >= 400 && error.status < 500 && error.status !== 408 && error.status !== 429) {
      return true
    }
    
    // Don't retry for data validation errors
    if (error.type === 'data' || error.type === 'data_validation' || error.type === 'json_parse') {
      return true
    }
    
    // Don't retry if offline (will retry when online event fires)
    if (error.type === 'offline') {
      return true
    }
    
    return false
  }

  /**
   * Handle network errors with appropriate user messaging
   * @param {Error} error - The error to handle
   * @private
   */
  handleNetworkError(error) {
    // Log the error with context
    this.logError('Network error occurred', {
      errorType: error.type,
      errorMessage: error.message,
      url: error.url || 'unknown',
      status: error.status || 'unknown',
      retryCount: this.state.retryCount || 0
    }, error)
    
    let message = this.options.errorText
    let showRetryButton = false
    
    switch (error.type) {
    case 'offline':
      message = 'You appear to be offline. Please check your internet connection and try again.'
      showRetryButton = true
      break
        
    case 'timeout':
      message = 'The request timed out. Please check your connection and try again.'
      showRetryButton = true
      break
        
    case 'network':
      message = 'Unable to connect to the server. Please check your internet connection and try again.'
      showRetryButton = true
      break
        
    case 'server':
      if (error.status >= 500) {
        message = 'The server is temporarily unavailable. Please try again in a few moments.'
        showRetryButton = true
      } else {
        message = 'The Patient Education Manager data is currently unavailable.'
      }
      break
        
    case 'data':
      message = 'The data received is corrupted. Please try again or contact support if the problem persists.'
      showRetryButton = true
      break
        
    case 'json_parse':
      message = 'The data received from the server is corrupted. Please try again or contact support if the problem persists.'
      showRetryButton = true
      break
        
    case 'data_validation':
      if (error.validationResult && error.validationResult.validManagers.length > 0) {
        // Partial data corruption - some managers are still valid
        message = 'Some data is corrupted, but the service is working with available information. You may experience limited coverage areas.'
        showRetryButton = false
      } else {
        // Complete data corruption
        message = 'The Patient Education Manager data is corrupted. Please try again or contact support.'
        showRetryButton = true
      }
      break
        
    default:
      message = this.options.errorText
      showRetryButton = true
    }
    
    // Add retry attempt information if applicable
    if (this.state.retryCount > 0) {
      message += ` (Tried ${this.state.retryCount + 1} time${this.state.retryCount > 0 ? 's' : ''})`
    }
    
    this.showError(message, error.type || 'general', showRetryButton)
  }

  /**
   * Sleep utility for retry delays
   * @param {number} ms - Milliseconds to sleep
   * @returns {Promise} Promise that resolves after the delay
   * @private
   */
  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  /**
   * Build a Map for O(1) specific zipcode lookups
   * Creates a mapping from zipcode to manager for all specificZips
   * Note: Current data structure uses only zipRanges, no specificZips
   * @private
   */
  buildSpecificZipMap() {
    if (!this.state.data || !this.state.data.managers) {
      this.state.specificZipMap = new Map()
      return
    }

    const zipMap = new Map()

    // Iterate through all managers and their territories
    for (const manager of this.state.data.managers) {
      if (manager.territories && Array.isArray(manager.territories)) {
        for (const territory of manager.territories) {
          // Check for specificZips (legacy format support)
          if (territory.specificZips && Array.isArray(territory.specificZips)) {
            // Add each specific zipcode to the map
            for (const zipcode of territory.specificZips) {
              const zipcodeNum = parseInt(zipcode, 10)
              if (!isNaN(zipcodeNum)) {
                // If zipcode already exists, log a warning (shouldn't happen in good data)
                if (zipMap.has(zipcodeNum)) {
                  console.warn(`Duplicate specific zipcode found: ${zipcodeNum}. Using first manager.`)
                } else {
                  zipMap.set(zipcodeNum, manager)
                }
              }
            }
          }
        }
      }
    }

    this.state.specificZipMap = zipMap
    console.log(`Built specific zipcode map with ${zipMap.size} entries`)
  }

  /**
   * Search for a manager using specific zipcode lookup (O(1) performance)
   * @param {number} zipcodeNum - The zipcode as a number
   * @returns {Object|null} Manager object if found, null otherwise
   * @private
   */
  searchSpecificZipcode(zipcodeNum) {
    // Validate input
    if (typeof zipcodeNum !== 'number' || isNaN(zipcodeNum)) {
      console.error('Invalid zip code number provided to searchSpecificZipcode:', zipcodeNum)
      return null
    }

    if (!this.state.specificZipMap) {
      console.warn('Specific zip code map not built. This may indicate a data loading issue.')
      return null
    }

    try {
      const manager = this.state.specificZipMap.get(zipcodeNum)
      
      // Validate found manager before returning
      if (manager) {
        const validation = this.validateManagerStructure(manager)
        if (!validation.success) {
          console.error('Found manager has invalid structure:', manager, validation.errors)
          return null
        }
      }
      
      return manager || null
    } catch (error) {
      console.error('Error during specific zip code lookup:', error)
      return null
    }
  }

  /**
   * Search for a manager using zipcode range matching
   * @param {number} zipcodeNum - The zipcode as a number
   * @returns {Object|null} Manager object if found, null otherwise
   * @private
   */
  searchZipcodeRanges(zipcodeNum) {
    if (!this.state.data || !this.state.data.managers) {
      return null
    }

    // Search through all managers and their territories for range matches
    for (const manager of this.state.data.managers) {
      if (manager.territories && Array.isArray(manager.territories)) {
        for (const territory of manager.territories) {
          if (territory.zipRanges && Array.isArray(territory.zipRanges)) {
            for (const range of territory.zipRanges) {
              // Validate range data
              if (!this.isValidZipcodeRange(range)) {
                console.warn('Invalid zip code range found:', range)
                continue
              }

              // Check if zipcode falls within this range
              if (zipcodeNum >= range.start && zipcodeNum <= range.end) {
                console.log(`Zipcode ${zipcodeNum} found in range ${range.start}-${range.end} for manager ${manager.name}`)
                return manager
              }
            }
          }
        }
      }
    }

    return null
  }

  /**
   * Validate zipcode range object
   * @param {Object} range - Range object to validate
   * @returns {boolean} True if valid, false otherwise
   * @private
   */
  isValidZipcodeRange(range) {
    if (!range || typeof range !== 'object') {
      return false
    }

    // Check if start and end properties exist and are numbers
    if (typeof range.start !== 'number' || typeof range.end !== 'number') {
      return false
    }

    // Check if start is less than or equal to end
    if (range.start > range.end) {
      return false
    }

    // Check if values are valid 5-digit zipcodes
    if (range.start < 0 || range.start > 99999 || range.end < 0 || range.end > 99999) {
      return false
    }

    return true
  }

  /**
   * Create a standardized SearchResponse object
   * @param {boolean} success - Whether the search was successful
   * @param {Object|null} manager - The manager object if found
   * @param {string|null} error - Error message if any
   * @param {string|null} fallbackContact - Fallback contact information
   * @returns {Object} SearchResponse object
   * @private
   */
  createSearchResponse(success, manager = null, error = null, fallbackContact = null) {
    return {
      success,
      manager,
      error,
      fallbackContact
    }
  }

  /**
   * Create a successful SearchResponse
   * @param {Object} manager - The found manager object
   * @returns {Object} SearchResponse object
   * @private
   */
  createSuccessResponse(manager) {
    return this.createSearchResponse(true, manager, null, null)
  }

  /**
   * Create an error SearchResponse
   * @param {string} error - Error message
   * @param {string|null} fallbackContact - Optional fallback contact
   * @returns {Object} SearchResponse object
   * @private
   */
  createErrorResponse(error, fallbackContact = null) {
    return this.createSearchResponse(false, null, error, fallbackContact)
  }

  /**
   * Handle different types of search errors with appropriate responses
   * @param {string} errorType - Type of error (validation, network, data, search)
   * @param {string} zipcode - The zipcode that caused the error
   * @param {Error|null} originalError - Original error object if available
   * @returns {Object} SearchResponse object
   * @private
   */
  handleSearchError(errorType, zipcode, originalError = null) {
    let errorMessage
    let fallbackContact = null

    switch (errorType) {
    case 'validation':
      errorMessage = 'Please enter a valid 5-digit zip code.'
      break
      
    case 'network':
      errorMessage = 'Unable to connect to the server. Please check your internet connection and try again.'
      fallbackContact = 'If the problem persists, please contact our Patient Support team at 1-800-XXX-XXXX'
      break
      
    case 'data':
      errorMessage = 'The Patient Education Manager data is currently unavailable. Please try again later.'
      fallbackContact = 'For immediate assistance, please contact our Patient Support team at 1-800-XXX-XXXX'
      break
      
    case 'corrupted':
      errorMessage = 'There was an issue with the manager data. Please try again or contact support.'
      fallbackContact = 'Please contact our Patient Support team at 1-800-XXX-XXXX for assistance'
      break
      
    case 'no-coverage':
      errorMessage = this.options.notFoundText
      fallbackContact = this.buildFallbackContactMessage()
      break
      
    case 'search':
    default:
      errorMessage = this.options.errorText
      fallbackContact = 'Please try again or contact our Patient Support team at 1-800-XXX-XXXX'
      break
    }

    // Log the error for debugging
    console.error(`Search error [${errorType}] for zip code ${zipcode}:`, originalError || errorMessage)

    // Show error to user
    this.showError(errorMessage, errorType)

    return this.createErrorResponse(errorMessage, fallbackContact)
  }

  /**
   * Build a comprehensive fallback contact message for no-coverage scenarios
   * @returns {string} Formatted fallback contact information
   * @private
   */
  buildFallbackContactMessage() {
    // Check if data has a fallback contact (from server)
    if (this.state.data && this.state.data.fallbackContact) {
      return this.state.data.fallbackContact
    }

    // Use configured fallback contact information
    const contact = this.options.fallbackContact
    
    return `${contact.alternativeText}, please contact our Patient Support team:

📞 Phone: ${contact.phoneDisplay}
📧 Email: ${contact.email}
🕒 Hours: ${contact.hours}
🌐 Website: ${contact.website}

Our Patient Support team can help you find alternative resources and connect you with healthcare providers in your area.`
  }

  /**
   * Enhanced data integrity validation with detailed error reporting
   * @param {Object} data - The data object to validate
   * @returns {Object} Validation result with success status, errors, and warnings
   * @private
   */
  validateDataIntegrity(data) {
    const validationResult = {
      success: false,
      errors: [],
      warnings: [],
      validManagers: [],
      invalidManagers: [],
      metadata: null
    }

    // Basic structure validation
    if (!data || typeof data !== 'object') {
      validationResult.errors.push({
        type: 'structure',
        field: 'root',
        message: 'Invalid data structure: Expected object, got ' + typeof data,
        severity: 'critical'
      })
      this.logValidationError('Data validation failed: Invalid data structure', { dataType: typeof data })
      return validationResult
    }

    // Validate managers array
    if (!data.managers) {
      validationResult.errors.push({
        type: 'missing_field',
        field: 'managers',
        message: 'Missing required field: managers',
        severity: 'critical'
      })
    } else if (!Array.isArray(data.managers)) {
      validationResult.errors.push({
        type: 'invalid_type',
        field: 'managers',
        message: 'Invalid managers field: Expected array, got ' + typeof data.managers,
        severity: 'critical'
      })
    } else if (data.managers.length === 0) {
      validationResult.warnings.push({
        type: 'empty_data',
        field: 'managers',
        message: 'No managers found in data',
        severity: 'warning'
      })
    }

    // If managers array is invalid, we can't proceed
    if (!Array.isArray(data.managers)) {
      this.logValidationError('Data validation failed: Invalid managers array', { 
        managersType: typeof data.managers,
        hasManagers: !!data.managers
      })
      return validationResult
    }

    // Validate individual managers with graceful degradation
    let validManagerCount = 0
    for (let i = 0; i < data.managers.length; i++) {
      const manager = data.managers[i]
      const managerValidation = this.validateManagerStructure(manager, i)
      
      if (managerValidation.success) {
        validationResult.validManagers.push(manager)
        validManagerCount++
      } else {
        validationResult.invalidManagers.push({
          index: i,
          manager: manager,
          errors: managerValidation.errors
        })
        // Add manager-specific errors to overall errors
        validationResult.errors.push(...managerValidation.errors.map(error => ({
          ...error,
          managerIndex: i,
          managerName: manager?.name || 'Unknown'
        })))
      }
    }

    // Validate metadata if present
    if (data.metadata) {
      const metadataValidation = this.validateMetadataStructure(data.metadata)
      if (metadataValidation.success) {
        validationResult.metadata = data.metadata
      } else {
        validationResult.warnings.push(...metadataValidation.errors)
      }
    }

    // Determine overall success
    if (validManagerCount === 0) {
      validationResult.errors.push({
        type: 'no_valid_data',
        field: 'managers',
        message: 'No valid managers found in dataset',
        severity: 'critical'
      })
      this.logValidationError('Data validation failed: No valid managers found', {
        totalManagers: data.managers.length,
        validManagers: validManagerCount,
        invalidManagers: validationResult.invalidManagers.length
      })
    } else {
      validationResult.success = true
      
      // Log warnings for partial data corruption
      if (validationResult.invalidManagers.length > 0) {
        validationResult.warnings.push({
          type: 'partial_corruption',
          field: 'managers',
          message: `${validationResult.invalidManagers.length} of ${data.managers.length} managers have validation errors`,
          severity: 'warning'
        })
        this.logValidationWarning('Partial data corruption detected', {
          totalManagers: data.managers.length,
          validManagers: validManagerCount,
          invalidManagers: validationResult.invalidManagers.length
        })
      }

      console.log(`Data validation passed: ${validManagerCount}/${data.managers.length} valid managers`)
    }

    return validationResult
  }

  /**
   * Enhanced manager structure validation with detailed error reporting
   * @param {Object} manager - Manager object to validate
   * @param {number} index - Index of manager in array for error reporting
   * @returns {Object} Validation result with success status and detailed errors
   * @private
   */
  validateManagerStructure(manager, index = null) {
    const validationResult = {
      success: false,
      errors: [],
      warnings: []
    }

    const managerContext = index !== null ? `Manager at index ${index}` : 'Manager'

    // Basic object validation
    if (!manager || typeof manager !== 'object') {
      validationResult.errors.push({
        type: 'invalid_type',
        field: 'manager',
        message: `${managerContext}: Expected object, got ${typeof manager}`,
        severity: 'critical'
      })
      return validationResult
    }

    // Required field validations
    const requiredFields = [
      { field: 'name', type: 'string', pattern: /^[A-Za-z\s\-.]+$/, minLength: 2, maxLength: 100 }
    ]

    for (const fieldDef of requiredFields) {
      const value = manager[fieldDef.field]
      
      if (value === undefined || value === null) {
        validationResult.errors.push({
          type: 'missing_field',
          field: fieldDef.field,
          message: `${managerContext}: Missing required field '${fieldDef.field}'`,
          severity: 'critical'
        })
        continue
      }

      if (typeof value !== fieldDef.type) {
        validationResult.errors.push({
          type: 'invalid_type',
          field: fieldDef.field,
          message: `${managerContext}: Field '${fieldDef.field}' must be ${fieldDef.type}, got ${typeof value}`,
          severity: 'critical'
        })
        continue
      }

      if (fieldDef.type === 'string') {
        if (fieldDef.minLength && value.length < fieldDef.minLength) {
          validationResult.errors.push({
            type: 'invalid_length',
            field: fieldDef.field,
            message: `${managerContext}: Field '${fieldDef.field}' must be at least ${fieldDef.minLength} characters, got ${value.length}`,
            severity: 'error'
          })
        }

        if (fieldDef.maxLength && value.length > fieldDef.maxLength) {
          validationResult.errors.push({
            type: 'invalid_length',
            field: fieldDef.field,
            message: `${managerContext}: Field '${fieldDef.field}' must be at most ${fieldDef.maxLength} characters, got ${value.length}`,
            severity: 'error'
          })
        }

        if (fieldDef.pattern && !fieldDef.pattern.test(value)) {
          validationResult.errors.push({
            type: 'invalid_format',
            field: fieldDef.field,
            message: `${managerContext}: Field '${fieldDef.field}' has invalid format: '${value}'`,
            severity: 'error'
          })
        }
      }
    }

    // Optional field validations
    if (manager.id !== undefined) {
      if (typeof manager.id !== 'string' || !/^manager-[0-9]+$/.test(manager.id)) {
        validationResult.warnings.push({
          type: 'invalid_format',
          field: 'id',
          message: `${managerContext}: Field 'id' should match pattern 'manager-[number]', got '${manager.id}'`,
          severity: 'warning'
        })
      }
    }

    if (manager.phone !== undefined) {
      if (typeof manager.phone !== 'string' || !/^\([0-9]{3}\) [0-9]{3}-[0-9]{4}$/.test(manager.phone)) {
        validationResult.warnings.push({
          type: 'invalid_format',
          field: 'phone',
          message: `${managerContext}: Field 'phone' has invalid format: '${manager.phone}'`,
          severity: 'warning'
        })
      }
    }

    if (manager.photoUrl !== undefined) {
      if (typeof manager.photoUrl !== 'string' || !/^\/images\/[a-zA-Z0-9\-_/]+\.(png|jpg|jpeg|webp)$/.test(manager.photoUrl)) {
        validationResult.warnings.push({
          type: 'invalid_format',
          field: 'photoUrl',
          message: `${managerContext}: Field 'photoUrl' has invalid format: '${manager.photoUrl}'`,
          severity: 'warning'
        })
      }
    }

    // Territories validation
    if (manager.territories) {
      if (!Array.isArray(manager.territories)) {
        validationResult.errors.push({
          type: 'invalid_type',
          field: 'territories',
          message: `${managerContext}: Field 'territories' must be array, got ${typeof manager.territories}`,
          severity: 'error'
        })
      } else {
        const territoryValidation = this.validateTerritoriesStructure(manager.territories, managerContext)
        validationResult.errors.push(...territoryValidation.errors)
        validationResult.warnings.push(...territoryValidation.warnings)
      }
    }

    // Determine success - critical errors prevent success
    const hasCriticalErrors = validationResult.errors.some(error => error.severity === 'critical')
    validationResult.success = !hasCriticalErrors

    return validationResult
  }

  /**
   * Validate territories structure
   * @param {Array} territories - Array of territory objects
   * @param {string} managerContext - Context string for error messages
   * @returns {Object} Validation result
   * @private
   */
  validateTerritoriesStructure(territories, managerContext) {
    const validationResult = {
      errors: [],
      warnings: []
    }

    if (territories.length === 0) {
      validationResult.warnings.push({
        type: 'empty_data',
        field: 'territories',
        message: `${managerContext}: No territories defined`,
        severity: 'warning'
      })
      return validationResult
    }

    let hasValidTerritory = false

    for (let i = 0; i < territories.length; i++) {
      const territory = territories[i]
      const territoryContext = `${managerContext}, territory ${i}`

      if (!territory || typeof territory !== 'object') {
        validationResult.errors.push({
          type: 'invalid_type',
          field: 'territory',
          message: `${territoryContext}: Expected object, got ${typeof territory}`,
          severity: 'error'
        })
        continue
      }

      // Validate state field
      if (!territory.state || typeof territory.state !== 'string') {
        validationResult.errors.push({
          type: 'missing_field',
          field: 'state',
          message: `${territoryContext}: Missing or invalid state field`,
          severity: 'error'
        })
      } else if (!/^[A-Z]{2}$/.test(territory.state)) {
        validationResult.errors.push({
          type: 'invalid_format',
          field: 'state',
          message: `${territoryContext}: State must be 2-letter code, got '${territory.state}'`,
          severity: 'error'
        })
      }

      // Validate zipRanges
      if (!territory.zipRanges || !Array.isArray(territory.zipRanges)) {
        validationResult.errors.push({
          type: 'missing_field',
          field: 'zipRanges',
          message: `${territoryContext}: Missing or invalid zipRanges array`,
          severity: 'error'
        })
      } else if (territory.zipRanges.length === 0) {
        validationResult.warnings.push({
          type: 'empty_data',
          field: 'zipRanges',
          message: `${territoryContext}: No zip code ranges defined`,
          severity: 'warning'
        })
      } else {
        // Validate individual zip ranges
        const rangeValidation = this.validateZipRanges(territory.zipRanges, territoryContext)
        validationResult.errors.push(...rangeValidation.errors)
        validationResult.warnings.push(...rangeValidation.warnings)
        
        if (rangeValidation.hasValidRanges) {
          hasValidTerritory = true
        }
      }

      // Validate specificZips if present
      if (territory.specificZips) {
        if (!Array.isArray(territory.specificZips)) {
          validationResult.errors.push({
            type: 'invalid_type',
            field: 'specificZips',
            message: `${territoryContext}: specificZips must be array, got ${typeof territory.specificZips}`,
            severity: 'error'
          })
        } else if (territory.specificZips.length > 0) {
          hasValidTerritory = true
          // Validate individual zipcodes
          for (const zip of territory.specificZips) {
            if (!Number.isInteger(zip) || zip < 1 || zip > 99999) {
              validationResult.errors.push({
                type: 'invalid_value',
                field: 'specificZips',
                message: `${territoryContext}: Invalid zip code ${zip}, must be integer between 1-99999`,
                severity: 'error'
              })
            }
          }
        }
      }
    }

    if (!hasValidTerritory) {
      validationResult.warnings.push({
        type: 'no_coverage',
        field: 'territories',
        message: `${managerContext}: No valid territory coverage found`,
        severity: 'warning'
      })
    }

    return validationResult
  }

  /**
   * Validate zipcode ranges
   * @param {Array} zipRanges - Array of zipcode range objects
   * @param {string} territoryContext - Context string for error messages
   * @returns {Object} Validation result
   * @private
   */
  validateZipRanges(zipRanges, territoryContext) {
    const validationResult = {
      errors: [],
      warnings: [],
      hasValidRanges: false
    }

    for (let i = 0; i < zipRanges.length; i++) {
      const range = zipRanges[i]
      const rangeContext = `${territoryContext}, range ${i}`

      if (!range || typeof range !== 'object') {
        validationResult.errors.push({
          type: 'invalid_type',
          field: 'zipRange',
          message: `${rangeContext}: Expected object, got ${typeof range}`,
          severity: 'error'
        })
        continue
      }

      // Validate start and end fields
      if (!Number.isInteger(range.start) || range.start < 1 || range.start > 99999) {
        validationResult.errors.push({
          type: 'invalid_value',
          field: 'start',
          message: `${rangeContext}: Invalid start value ${range.start}, must be integer between 1-99999`,
          severity: 'error'
        })
      }

      if (!Number.isInteger(range.end) || range.end < 1 || range.end > 99999) {
        validationResult.errors.push({
          type: 'invalid_value',
          field: 'end',
          message: `${rangeContext}: Invalid end value ${range.end}, must be integer between 1-99999`,
          severity: 'error'
        })
      }

      // Validate range logic
      if (Number.isInteger(range.start) && Number.isInteger(range.end)) {
        if (range.start > range.end) {
          validationResult.errors.push({
            type: 'invalid_range',
            field: 'zipRange',
            message: `${rangeContext}: Invalid range ${range.start}-${range.end} (start > end)`,
            severity: 'error'
          })
        } else {
          validationResult.hasValidRanges = true
        }
      }
    }

    return validationResult
  }

  /**
   * Validate metadata structure
   * @param {Object} metadata - Metadata object to validate
   * @returns {Object} Validation result
   * @private
   */
  validateMetadataStructure(metadata) {
    const validationResult = {
      success: true,
      errors: [],
      warnings: []
    }

    if (!metadata || typeof metadata !== 'object') {
      validationResult.errors.push({
        type: 'invalid_type',
        field: 'metadata',
        message: 'Metadata must be object, got ' + typeof metadata,
        severity: 'warning'
      })
      validationResult.success = false
      return validationResult
    }

    // Optional metadata field validations
    const optionalFields = [
      { field: 'totalStates', type: 'number', min: 1, max: 60 },
      { field: 'totalManagers', type: 'number', min: 1, max: 100 },
      { field: 'totalZipcodes', type: 'number', min: 1, max: 100000 },
      { field: 'version', type: 'string', pattern: /^[0-9]+\.[0-9]+\.[0-9]+$/ }
    ]

    for (const fieldDef of optionalFields) {
      const value = metadata[fieldDef.field]
      
      if (value !== undefined) {
        if (typeof value !== fieldDef.type) {
          validationResult.warnings.push({
            type: 'invalid_type',
            field: fieldDef.field,
            message: `Metadata field '${fieldDef.field}' should be ${fieldDef.type}, got ${typeof value}`,
            severity: 'warning'
          })
        } else if (fieldDef.type === 'number') {
          if (fieldDef.min && value < fieldDef.min) {
            validationResult.warnings.push({
              type: 'invalid_value',
              field: fieldDef.field,
              message: `Metadata field '${fieldDef.field}' should be at least ${fieldDef.min}, got ${value}`,
              severity: 'warning'
            })
          }
          if (fieldDef.max && value > fieldDef.max) {
            validationResult.warnings.push({
              type: 'invalid_value',
              field: fieldDef.field,
              message: `Metadata field '${fieldDef.field}' should be at most ${fieldDef.max}, got ${value}`,
              severity: 'warning'
            })
          }
        } else if (fieldDef.pattern && !fieldDef.pattern.test(value)) {
          validationResult.warnings.push({
            type: 'invalid_format',
            field: fieldDef.field,
            message: `Metadata field '${fieldDef.field}' has invalid format: '${value}'`,
            severity: 'warning'
          })
        }
      }
    }

    return validationResult
  }

  /**
   * Log validation errors for monitoring and debugging
   * @param {string} message - Error message
   * @param {Object} context - Additional context for logging
   * @private
   */
  logValidationError(message, context = {}) {
    const logEntry = {
      timestamp: new Date().toISOString(),
      level: 'error',
      category: 'data_validation',
      message: message,
      context: {
        url: this.options.dataUrl,
        userAgent: navigator.userAgent,
        ...context
      }
    }

    console.error('Data Validation Error:', logEntry)
    
    // Store in session storage for potential debugging
    try {
      const existingLogs = JSON.parse(sessionStorage.getItem('pef_validation_logs') || '[]')
      existingLogs.push(logEntry)
      // Keep only last 10 entries
      if (existingLogs.length > 10) {
        existingLogs.splice(0, existingLogs.length - 10)
      }
      sessionStorage.setItem('pef_validation_logs', JSON.stringify(existingLogs))
    } catch (e) {
      // Ignore storage errors
    }
  }

  /**
   * Log validation warnings for monitoring
   * @param {string} message - Warning message
   * @param {Object} context - Additional context for logging
   * @private
   */
  logValidationWarning(message, context = {}) {
    const logEntry = {
      timestamp: new Date().toISOString(),
      level: 'warning',
      category: 'data_validation',
      message: message,
      context: {
        url: this.options.dataUrl,
        ...context
      }
    }

    console.warn('Data Validation Warning:', logEntry)
  }

  /**
   * Search for a Patient Education Manager by zipcode
   * Main orchestration method that coordinates specific and range lookups
   * @param {string} zipcode - The zipcode to search for
   * @returns {Object} SearchResponse object with success, manager, error, and fallbackContact properties
   */
  async searchManager(zipcode) {
    console.log(`Starting search for zip code: ${zipcode}`)
    
    // Validate input zipcode format first
    if (!this.validateZipcode(zipcode, false)) {
      return this.handleSearchError('validation', zipcode)
    }

    // Ensure data is loaded
    if (!this.state.data) {
      try {
        console.log('Loading manager data...')
        await this.loadData()
      } catch (error) {
        // Determine error type based on error characteristics
        if (error.name === 'TypeError' && error.message.includes('fetch')) {
          return this.handleSearchError('network', zipcode, error)
        } else if (error.message.includes('corrupted')) {
          return this.handleSearchError('corrupted', zipcode, error)
        } else {
          return this.handleSearchError('data', zipcode, error)
        }
      }
    }

    // Convert zipcode to number for comparison
    const zipcodeNum = parseInt(zipcode, 10)
    
    if (isNaN(zipcodeNum)) {
      return this.handleSearchError('validation', zipcode)
    }

    try {
      this.setLoading(true)
      this.clearError()

      // Step 1: Try specific zipcode lookup using Map (O(1) performance)
      console.log(`Searching specific zipcodes for: ${zipcodeNum}`)
      const specificManager = this.searchSpecificZipcode(zipcodeNum)
      if (specificManager) {
        console.log(`Found manager via specific zipcode: ${specificManager.name}`)
        this.state.lastSearchResult = specificManager
        this.displayResults(specificManager)
        return this.createSuccessResponse(specificManager)
      }

      // Step 2: If no specific zipcode match, search through zipcode ranges
      console.log(`No specific zip code match. Searching ranges for: ${zipcodeNum}`)
      const rangeManager = this.searchZipcodeRanges(zipcodeNum)
      if (rangeManager) {
        console.log(`Found manager via zip code range: ${rangeManager.name}`)
        this.state.lastSearchResult = rangeManager
        this.displayResults(rangeManager)
        return this.createSuccessResponse(rangeManager)
      }

      // Step 3: No manager found in either specific zipcodes or ranges
      this.state.lastSearchResult = null
      return this.handleSearchError('no-coverage', zipcode)

    } catch (error) {
      return this.handleSearchError('search', zipcode, error)
      
    } finally {
      this.setLoading(false)
    }
  }

  /**
   * Display Patient Education Manager information in the UI
   * @param {Object} manager - The manager object to display
   */
  displayResults(manager) {
    if (!manager || !this.elements.resultsContainer) {
      console.error('Cannot display results: missing manager data or results container')
      return
    }

    // Clear any existing errors
    this.clearError()

    // Format phone number if available
    const formattedPhone = manager.phone ? this.formatPhoneNumber(manager.phone) : null

    // Create the results HTML matching the CSS structure
    const resultsHTML = `
      <div class="search-results-header">
        <p class="search-result-intro">The following Patient Education Manager is located near you:</p>
      </div>
      
      <div class="manager-card" role="region" aria-label="Patient Education Manager Information">
        <div class="manager-header">
          ${manager.photoUrl ? `
            <img src="${this.escapeHtml(manager.photoUrl)}" 
                 alt="Photo of ${this.escapeHtml(manager.name)}" 
                 class="manager-photo"
                 loading="lazy"
                 onerror="this.style.display='none'" />
          ` : `
            <div class="manager-photo manager-photo-placeholder" aria-hidden="true">
              <span class="photo-initials">${this.getInitials(manager.name)}</span>
            </div>
          `}
          
          <div class="manager-info">
            <h3 class="manager-name">${this.escapeHtml(manager.name)}</h3>
            <p class="manager-title">Patient Education Manager</p>
            <a href="${this.escapeHtml(this.options.enrollmentUrl)}" class="enroll-button enroll-button-two" target="_blank" rel="noopener noreferrer" aria-label="Enroll in patient education services (opens in new tab)">
              <span class="enroll-text" data-override="enroll-button-text">Enroll</span>
                <svg width="10" height="16" viewBox="0 0 10 16" fill="none" class="chevron-right" xmlns="http://www.w3.org/2000/svg">
                     <path d="M1.61079 1.88881L7.83301 8.11103L1.61079 14.3333" stroke="currentColor" stroke-width="2.66667"/>
                </svg>
            </a>
          </div>
        </div>
        
        <div class="manager-contact">
          ${formattedPhone ? `
            <div class="contact-item">
              <svg class="contact-icon" aria-hidden="true" viewBox="0 0 24 24" fill="currentColor">
                <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
              </svg>
              <div class="contact-info">
                <div class="contact-label">Phone</div>
                <div class="contact-value">
                  <a href="tel:${formattedPhone.replace(/\D/g, '')}" 
                     aria-label="Call ${this.escapeHtml(manager.name)} at ${formattedPhone}">
                    ${formattedPhone}
                  </a>
                </div>
              </div>
            </div>
          ` : `
            <div class="contact-item">
              <svg class="contact-icon" aria-hidden="true" viewBox="0 0 24 24" fill="currentColor">
                <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
              </svg>
              <div class="contact-info">
                <div class="contact-label">Phone</div>
                <div class="contact-value">
                  <a href="tel:${this.options.fallbackContact.phone.replace(/\D/g, '')}" 
                     aria-label="Call ${this.options.fallbackContact.phoneDisplay}">
                    ${this.options.fallbackContact.phoneDisplay}
                  </a>
                </div>
              </div>
            </div>
          `}
          
          ${manager.email ? `
            <div class="contact-item">
              <svg class="contact-icon" aria-hidden="true" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
              </svg>
              <div class="contact-info">
                <div class="contact-label">Email</div>
                <div class="contact-value">
                  <a href="mailto:${this.escapeHtml(manager.email)}" 
                     aria-label="Email ${this.escapeHtml(manager.name)}">
                    ${this.escapeHtml(manager.email)}
                  </a>
                </div>
              </div>
            </div>
          ` : ''}
          
          ${manager.territory ? `
            <div class="contact-item">
              <svg class="contact-icon" aria-hidden="true" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
              </svg>
              <div class="contact-info">
                <div class="contact-label">Territory</div>
                <div class="contact-value">${this.escapeHtml(manager.territory)}</div>
              </div>
            </div>
          ` : ''}
        </div>
        
        ${manager.coverageAreas && manager.coverageAreas.length > 0 ? `
          <div class="manager-coverage">
            <div class="coverage-label">Coverage Areas</div>
            <div class="coverage-areas">${this.escapeHtml(manager.coverageAreas.join(', '))}</div>
          </div>
        ` : ''}
        </div>
    <button type="button" class="new-search-button" aria-label="Start a new search">
        <span>&lt;</span><span>Back</span>
    </button>
    `

    // Hide the search form and intro elements
    this.hideSearchForm()

    // Insert the results
    this.elements.resultsContainer.innerHTML = resultsHTML
    this.elements.resultsContainer.style.display = 'block'
    
    // Add success class to container for styling
    this.container.classList.add('success', 'results-shown')
    this.container.classList.remove('error', 'loading')

    // Bind the new search button event
    this.bindNewSearchButton()

    // Scroll to results for better UX
    this.scrollToResults()

    console.log('Results displayed for manager:', manager.name)
  }

  /**
   * Format phone number to (XXX) XXX-XXXX format
   * @param {string} phone - Raw phone number
   * @returns {string} Formatted phone number
   * @private
   */
  formatPhoneNumber(phone) {
    if (!phone) return ''
    
    // Remove all non-digits
    const digits = phone.replace(/\D/g, '')
    
    // Format as (XXX) XXX-XXXX if we have 10 digits
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
    }
    
    // Return original if not 10 digits
    return phone
  }

  /**
   * Escape HTML to prevent XSS attacks
   * @param {string} text - Text to escape
   * @returns {string} Escaped text
   * @private
   */
  escapeHtml(text) {
    if (!text) return ''
    
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  /**
   * Get initials from a full name for placeholder display
   * @param {string} name - Full name
   * @returns {string} Initials (max 2 characters)
   * @private
   */
  getInitials(name) {
    if (!name) return '??'
    
    const words = name.trim().split(/\s+/)
    if (words.length === 1) {
      return words[0].charAt(0).toUpperCase()
    }
    
    return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase()
  }

  /**
   * Hide the search form and intro elements when showing results
   * @private
   */
  hideSearchForm() {
    // Hide the search form
    if (this.elements.form) {
      this.elements.form.style.display = 'none'
    }
  }

  /**
   * Show the search form and intro elements when clearing results
   * @private
   */
  showSearchForm() {
    // Show the search form
    if (this.elements.form) {
      this.elements.form.style.display = 'flex'
    }

    // Show the search intro text
    const searchIntro = this.container.querySelector('.search-intro')
    if (searchIntro) {
      searchIntro.style.display = 'block'
    }

    // Show the search divider image
    const searchDivider = this.container.querySelector('.search-divider')
    if (searchDivider) {
      searchDivider.style.display = 'block'
    }
  }

  /**
   * Bind event listener to the new search button
   * @private
   */
  bindNewSearchButton() {
    const newSearchButton = this.elements.resultsContainer.querySelector('.new-search-button')
    if (newSearchButton) {
      newSearchButton.addEventListener('click', () => {
        this.startNewSearch()
      })
    }
  }

  /**
   * Start a new search by clearing results and showing the form
   * @private
   */
  startNewSearch() {
    // Clear the current search state
    this.clearResults()
    this.clearError()
    this.clearValidationUI()
    
    // Reset the input value and focus
    if (this.elements.input) {
      this.elements.input.value = ''
      this.state.currentZipcode = ''
      this.elements.input.focus()
    }

    // Show the search form again
    this.showSearchForm()

    // Remove results-shown class
    this.container.classList.remove('results-shown', 'success')

    // Announce to screen readers
    this.announceToScreenReader('Search form is ready. Please enter a new zip code.')
  }

  /**
   * Scroll to results container for better user experience
   * @private
   */
  scrollToResults() {
    if (this.elements.resultsContainer) {
      // Small delay to ensure content is rendered
      setTimeout(() => {
        this.elements.resultsContainer.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        })
      }, 100)
    }
  }

  /**
   * Enhanced error recovery and user action guidance
   * Provides comprehensive retry mechanisms and clear guidance for error resolution
   * @param {string} message - The error message to display
   * @param {string} type - The type of error (general, network, validation, etc.)
   * @param {boolean} showRetryButton - Whether to show a retry button
   * @param {Object} recoveryOptions - Additional recovery options
   * @private
   */
  showError(message, type = 'general', showRetryButton = false, recoveryOptions = {}) {
    if (!this.elements.resultsContainer) return

    // Clear results but don't show the form yet - we'll handle that in the error display
    if (this.elements.resultsContainer) {
      this.elements.resultsContainer.innerHTML = ''
      this.elements.resultsContainer.style.display = 'none'
    }
    this.container.classList.remove('success', 'error', 'no-coverage', 'results-shown')
    
    // Handle no-coverage scenarios with special display
    if (type === 'no-coverage') {
      this.displayNoCoverageMessage(message)
      return
    }
    
    // Determine recovery actions based on error type
    const recoveryActions = this.getRecoveryActions(type, recoveryOptions)
    
    // Create action buttons HTML
    const actionButtonsHtml = this.createActionButtons(recoveryActions, showRetryButton)
    
    // Create helpful tips based on error type
    const helpfulTips = this.getHelpfulTips(type)
    
    // Hide the search form and intro elements
    this.hideSearchForm()
    
    // Show error in results container with enhanced recovery options
    this.elements.resultsContainer.innerHTML = `
      <div class="search-results-header">
        <button type="button" class="new-search-button" aria-label="Start a new search">
          <svg class="back-icon" aria-hidden="true" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
          </svg>
          New Search
        </button>
        <p class="search-result-intro">${this.getErrorTitle(type)}</p>
      </div>
      
      <div class="error-message error-${type}" role="alert" aria-live="assertive">
        <div class="error-header">
          <svg class="error-icon" aria-hidden="true" viewBox="0 0 24 24" fill="currentColor">
            <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
          </svg>
          <div class="error-title">${this.getErrorTitle(type)}</div>
        </div>
        
        <div class="error-content">
          <div class="error-text">${message}</div>
          
          ${helpfulTips ? `
            <div class="error-tips">
              <div class="tips-title">💡 Helpful Tips:</div>
              <ul class="tips-list">
                ${helpfulTips.map(tip => `<li>${this.escapeHtml(tip)}</li>`).join('')}
              </ul>
            </div>
          ` : ''}
          
          ${actionButtonsHtml ? `
            <div class="error-actions">
              ${actionButtonsHtml}
            </div>
          ` : ''}
          
          ${this.shouldShowProgressIndicator(type) ? `
            <div class="recovery-progress" style="display: none;">
              <div class="progress-bar">
                <div class="progress-fill"></div>
              </div>
              <div class="progress-text">Attempting recovery...</div>
            </div>
          ` : ''}
        </div>
      </div>
    `
    this.elements.resultsContainer.style.display = 'block'
    
    // Bind event listeners for action buttons
    this.bindErrorActionListeners()
    
    // Bind the new search button event
    this.bindNewSearchButton()
    
    // Add error class to container for styling
    this.container.classList.add('error', `error-${type}`, 'results-shown')
    this.container.classList.remove('success', 'loading')
    
    // Store error state for recovery tracking
    this.state.lastError = {
      type,
      message,
      timestamp: Date.now(),
      recoveryAttempts: this.state.lastError?.recoveryAttempts || 0
    }
  }

  /**
   * Get appropriate error title based on error type
   * @param {string} type - The error type
   * @returns {string} The error title
   * @private
   */
  getErrorTitle(type) {
    const titles = {
      'network': 'Connection Problem',
      'timeout': 'Request Timed Out',
      'server': 'Server Error',
      'json_parse': 'Data Format Error',
      'data_validation': 'Data Quality Issue',
      'validation': 'Input Error',
      'offline': 'No Internet Connection',
      'general': 'Search Error'
    }
    return titles[type] || 'Error'
  }

  /**
   * Get recovery actions based on error type
   * @param {string} type - The error type
   * @param {Object} options - Additional recovery options
   * @returns {Array} Array of recovery action objects
   * @private
   */
  getRecoveryActions(type, options = {}) {
    const actions = []
    
    switch (type) {
    case 'network':
    case 'timeout':
    case 'server':
      actions.push(
        { id: 'retry', label: 'Try Again', primary: true, icon: 'refresh' },
        { id: 'check-connection', label: 'Check Connection', icon: 'wifi' }
      )
      break
        
    case 'offline':
      actions.push(
        { id: 'check-connection', label: 'Check Connection', primary: true, icon: 'wifi' }
      )
      break
        
    case 'json_parse':
    case 'data_validation':
      actions.push(
        { id: 'retry', label: 'Reload Data', primary: true, icon: 'refresh' },
        { id: 'contact-support', label: 'Contact Support', icon: 'support' }
      )
      break
        
    case 'validation':
      actions.push(
        { id: 'clear-input', label: 'Clear & Try Again', primary: true, icon: 'clear' }
      )
      break
        
    default:
      if (options.allowRetry !== false) {
        actions.push(
          { id: 'retry', label: 'Try Again', primary: true, icon: 'refresh' }
        )
      }
    }
    
    // Add common actions
    if (type !== 'validation') {
      actions.push(
        { id: 'contact-support', label: 'Get Help', icon: 'support' }
      )
    }
    
    return actions
  }

  /**
   * Create action buttons HTML
   * @param {Array} actions - Array of action objects
   * @param {boolean} showRetryButton - Legacy retry button flag
   * @returns {string} HTML for action buttons
   * @private
   */
  createActionButtons(actions, showRetryButton) {
    if (!actions.length && !showRetryButton) return ''
    
    // Handle legacy retry button
    if (showRetryButton && !actions.some(a => a.id === 'retry')) {
      actions.unshift({ id: 'retry', label: 'Try Again', primary: true, icon: 'refresh' })
    }
    
    return actions.map(action => {
      const iconSvg = this.getActionIcon(action.icon)
      const buttonClass = `action-button ${action.primary ? 'primary' : 'secondary'}`
      
      return `
        <button type="button" 
                class="${buttonClass}" 
                data-action="${action.id}"
                aria-label="${action.label}">
          ${iconSvg}
          <span class="button-text">${action.label}</span>
        </button>
      `
    }).join('')
  }

  /**
   * Get SVG icon for action buttons
   * @param {string} iconType - The icon type
   * @returns {string} SVG icon HTML
   * @private
   */
  getActionIcon(iconType) {
    const icons = {
      'refresh': '<svg class="action-icon" viewBox="0 0 24 24" fill="currentColor"><path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/></svg>',
      'wifi': '<svg class="action-icon" viewBox="0 0 24 24" fill="currentColor"><path d="M1 9l2 2c4.97-4.97 13.03-4.97 18 0l2-2C16.93 2.93 7.07 2.93 1 9zm8 8l3 3 3-3c-1.65-1.66-4.34-1.66-6 0zm-4-4l2 2c2.76-2.76 7.24-2.76 10 0l2-2C15.14 9.14 8.87 9.14 5 13z"/></svg>',
      'clear': '<svg class="action-icon" viewBox="0 0 24 24" fill="currentColor"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>',
      'support': '<svg class="action-icon" viewBox="0 0 24 24" fill="currentColor"><path d="M11 18h2v-2h-2v2zm1-16C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm0-14c-2.21 0-4 1.79-4 4h2c0-1.1.9-2 2-2s2 .9 2 2c0 2-3 1.75-3 5h2c0-2.25 3-2.5 3-5 0-2.21-1.79-4-4-4z"/></svg>'
    }
    return icons[iconType] || icons['refresh']
  }

  /**
   * Get helpful tips based on error type
   * @param {string} type - The error type
   * @returns {Array|null} Array of helpful tips or null
   * @private
   */
  getHelpfulTips(type) {
    const tips = {
      'network': [
        'Check your internet connection',
        'Try refreshing the page',
        'Disable VPN if you\'re using one'
      ],
      'timeout': [
        'Your connection may be slow',
        'Try again in a few moments',
        'Check if other websites are loading'
      ],
      'offline': [
        'Check your WiFi or mobile data connection',
        'Try opening another website to test connectivity',
        'Contact your internet service provider if problems persist'
      ],
      'validation': [
        'Enter exactly 5 digits (example: 12345)',
        'Use only numbers 0-9',
        'Don\'t include spaces, dashes, or letters'
      ],
      'json_parse': [
        'This appears to be a temporary server issue',
        'Try again in a few moments',
        'Contact support if the problem continues'
      ],
      'data_validation': [
        'Some data may be temporarily unavailable',
        'Try searching for a different zip code',
        'Contact support for assistance'
      ]
    }
    return tips[type] || null
  }

  /**
   * Check if progress indicator should be shown for this error type
   * @param {string} type - The error type
   * @returns {boolean} Whether to show progress indicator
   * @private
   */
  shouldShowProgressIndicator(type) {
    return ['network', 'timeout', 'server', 'json_parse', 'data_validation'].includes(type)
  }

  /**
   * Bind event listeners for error action buttons
   * @private
   */
  bindErrorActionListeners() {
    if (!this.elements.resultsContainer) return
    
    const actionButtons = this.elements.resultsContainer.querySelectorAll('.action-button')
    actionButtons.forEach(button => {
      button.addEventListener('click', this.handleErrorAction.bind(this))
    })
  }

  /**
   * Handle error action button clicks
   * @param {Event} event - The click event
   * @private
   */
  async handleErrorAction(event) {
    const action = event.currentTarget.getAttribute('data-action')
    const button = event.currentTarget
    
    // Disable button during action
    button.disabled = true
    button.classList.add('loading')
    
    try {
      await this.executeErrorAction(action)
    } catch (error) {
      console.error('Error action failed:', error)
    } finally {
      // Re-enable button
      button.disabled = false
      button.classList.remove('loading')
    }
  }

  /**
   * Execute specific error recovery actions
   * @param {string} action - The action to execute
   * @private
   */
  async executeErrorAction(action) {
    switch (action) {
    case 'retry':
      await this.handleRetryAction()
      break
        
    case 'check-connection':
      this.handleCheckConnectionAction()
      break
        
    case 'clear-input':
      this.handleClearInputAction()
      break
        
    case 'contact-support':
      this.handleContactSupportAction()
      break
        
    default:
      console.warn('Unknown error action:', action)
    }
  }

  /**
   * Handle retry action with enhanced feedback
   * @private
   */
  async handleRetryAction() {
    // Show progress indicator
    this.showRecoveryProgress('Retrying...')
    
    // Increment retry attempts
    if (this.state.lastError) {
      this.state.lastError.recoveryAttempts = (this.state.lastError.recoveryAttempts || 0) + 1
    }
    
    try {
      // Clear current error state
      this.clearError()
      this.clearResults()
      
      // Reset data state to force reload
      this.state.data = null
      this.state.specificZipMap = null
      
      // Attempt to reload data
      await this.loadData()
      
      // Show success message
      this.showRecoverySuccess('Data loaded successfully!')
      
      // If there was a previous search, retry it
      if (this.state.currentZipcode && this.validateZipcode(this.state.currentZipcode, false)) {
        setTimeout(() => {
          this.searchManager(this.state.currentZipcode)
        }, 1000)
      }
      
    } catch (error) {
      console.error('Retry failed:', error)
      this.hideRecoveryProgress()
      // Error will be handled by loadData method
    }
  }

  /**
   * Handle check connection action
   * @private
   */
  handleCheckConnectionAction() {
    // Test connection by making a simple request
    this.showRecoveryProgress('Checking connection...')
    
    fetch('/favicon.ico', { method: 'HEAD', cache: 'no-cache' })
      .then(response => {
        if (response.ok) {
          this.showRecoverySuccess('Connection is working! Try your search again.')
        } else {
          this.showRecoveryError('Connection test failed. Please check your internet.')
        }
      })
      .catch(() => {
        this.showRecoveryError('No internet connection detected. Please check your network settings.')
      })
      .finally(() => {
        setTimeout(() => this.hideRecoveryProgress(), 3000)
      })
  }

  /**
   * Handle clear input action
   * @private
   */
  handleClearInputAction() {
    if (this.elements.input) {
      this.elements.input.value = ''
      this.elements.input.focus()
      this.state.currentZipcode = ''
      this.clearError()
      this.clearResults()
      this.clearValidationUI()
      this.showRecoverySuccess('Input cleared. Please enter a valid 5-digit zip code.')
      setTimeout(() => this.hideRecoveryProgress(), 2000)
    }
  }

  /**
   * Handle contact support action
   * @private
   */
  handleContactSupportAction() {
    const contact = this.options.fallbackContact
    
    // Create support contact modal or redirect
    const supportInfo = `
      <div class="support-contact-info">
        <h4>Get Help</h4>
        <p>Contact our Patient Support team for assistance:</p>
        <div class="contact-methods">
          <div class="contact-method">
            <strong>Phone:</strong> 
            <a href="tel:${contact.phone.replace(/\D/g, '')}">${contact.phoneDisplay}</a>
          </div>
          <div class="contact-method">
            <strong>Email:</strong> 
            <a href="mailto:${contact.email}">${contact.email}</a>
          </div>
          <div class="contact-method">
            <strong>Hours:</strong> ${contact.hours}
          </div>
        </div>
      </div>
    `
    
    // Replace error content with support info
    const errorContent = this.elements.resultsContainer.querySelector('.error-content')
    if (errorContent) {
      errorContent.innerHTML = supportInfo
    }
  }

  /**
   * Show recovery progress indicator
   * @param {string} message - Progress message
   * @private
   */
  showRecoveryProgress(message) {
    const progressElement = this.elements.resultsContainer?.querySelector('.recovery-progress')
    if (progressElement) {
      const textElement = progressElement.querySelector('.progress-text')
      if (textElement) {
        textElement.textContent = message
      }
      progressElement.style.display = 'block'
    }
  }

  /**
   * Show recovery success message
   * @param {string} message - Success message
   * @private
   */
  showRecoverySuccess(message) {
    this.showValidationMessage(message, 'success')
    this.hideRecoveryProgress()
  }

  /**
   * Show recovery error message
   * @param {string} message - Error message
   * @private
   */
  showRecoveryError(message) {
    this.showValidationMessage(message, 'error')
    this.hideRecoveryProgress()
  }

  /**
   * Hide recovery progress indicator
   * @private
   */
  hideRecoveryProgress() {
    const progressElement = this.elements.resultsContainer?.querySelector('.recovery-progress')
    if (progressElement) {
      progressElement.style.display = 'none'
    }
  }

  /**
   * Enhanced automatic error clearing when users take corrective action
   * @private
   */
  clearErrorOnUserAction() {
    // Clear errors when user starts typing (already implemented in handleInput)
    // Clear errors when user focuses on input
    if (this.elements.input) {
      this.elements.input.addEventListener('focus', () => {
        if (this.container.classList.contains('error')) {
          this.clearError()
          this.showValidationMessage('Enter your 5-digit zip code to search', 'info')
        }
      })
    }
  }

  /**
   * Enhanced retry button click handler (legacy support)
   * @private
   */
  async handleRetryClick() {
    await this.handleRetryAction()
  }

  /**
   * Display a user-friendly no-coverage message with fallback contact information
   * @param {string} message - The primary no-coverage message
   * @private
   */
  displayNoCoverageMessage(message) {
    const contact = this.options.fallbackContact
    
    // Create a more user-friendly no-coverage display
    const noCoverageHTML = `
      <div class="search-results-header">
        <button type="button" class="new-search-button" aria-label="Start a new search">
          <svg class="back-icon" aria-hidden="true" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
          </svg>
          New Search
        </button>
        <p class="search-result-intro">No Coverage Available</p>
      </div>
      
      <div class="no-coverage-message" role="region" aria-label="No Coverage Information">
        <div class="no-coverage-header">
          <svg class="no-coverage-icon" aria-hidden="true" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
          </svg>
          <div class="no-coverage-title">Coverage Not Available</div>
        </div>
        
        <div class="no-coverage-content">
          <p class="no-coverage-text">${this.escapeHtml(message)}</p>
          
          <div class="fallback-contact-section">
            <h4 class="fallback-contact-title">${this.escapeHtml(contact.alternativeText)}</h4>
            
            <div class="contact-options">
              <div class="contact-option">
                <svg class="contact-icon" aria-hidden="true" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                </svg>
                <div class="contact-details">
                  <div class="contact-label">Call Patient Support</div>
                  <div class="contact-value">
                    <a href="tel:${contact.phone.replace(/\D/g, '')}" 
                       aria-label="Call Patient Support at ${contact.phoneDisplay}">
                      ${this.escapeHtml(contact.phoneDisplay)}
                    </a>
                  </div>
                  <div class="contact-hours">${this.escapeHtml(contact.hours)}</div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    `
    
    // Hide the search form and intro elements
    this.hideSearchForm()
    
    this.elements.resultsContainer.innerHTML = noCoverageHTML
    this.elements.resultsContainer.style.display = 'block'
    
    // Add no-coverage class to container for styling
    this.container.classList.add('no-coverage', 'results-shown')
    this.container.classList.remove('success', 'loading', 'error')
    
    // Bind the new search button event
    this.bindNewSearchButton()
  }

  /**
   * Clear error messages
   */
  clearError() {
    // Errors are now shown in results container, so clearing results clears errors
    this.container.classList.remove('error', 'no-coverage')
  }

  /**
   * Clear all validation UI states
   */
  clearValidationUI() {
    if (this.elements.input) {
      this.elements.input.classList.remove('valid', 'invalid', 'incomplete', 'validation-error', 'validation-success')
      this.elements.input.setAttribute('aria-invalid', 'false')
    }
    this.container.classList.remove('input-valid', 'input-invalid', 'input-incomplete', 'has-validation-error', 'has-validation-success')
    this.clearValidationMessage()
    this.clearInlineValidationError()
  }

  /**
   * Show validation message for screen readers
   * @param {string} message - The validation message to display
   * @param {string} type - The type of message (success, error, info)
   * @private
   */
  showValidationMessage(message, type = 'info') {
    if (!this.elements.validationMessageContainer) return

    this.elements.validationMessageContainer.innerHTML = `
      <span class="validation-message validation-${type}" role="status">
        ${message}
      </span>
    `
    this.elements.validationMessageContainer.style.display = 'block'
  }

  /**
   * Clear validation message
   * @private
   */
  clearValidationMessage() {
    if (this.elements.validationMessageContainer) {
      this.elements.validationMessageContainer.innerHTML = ''
      this.elements.validationMessageContainer.style.display = 'none'
    }
  }

  /**
   * Clear results display
   */
  clearResults() {
    if (this.elements.resultsContainer) {
      this.elements.resultsContainer.innerHTML = ''
      this.elements.resultsContainer.style.display = 'none'
    }
    this.container.classList.remove('success', 'error', 'no-coverage', 'results-shown')
    
    // Show the search form again when results are cleared
    this.showSearchForm()
  }

  /**
   * Set loading state
   */
  setLoading(isLoading) {
    this.state.isLoading = isLoading
    
    if (isLoading) {
      this.container.classList.add('loading')
      this.container.classList.remove('success', 'error')
      
      if (this.elements.submitButton) {
        this.elements.submitButton.disabled = true
        this.elements.submitButton.classList.add('loading')
        // Store original text if not already stored
        if (!this.elements.submitButton.getAttribute('data-original-text')) {
          this.elements.submitButton.setAttribute('data-original-text', this.elements.submitButton.textContent)
        }
        this.elements.submitButton.textContent = this.options.loadingText
      }
    } else {
      this.container.classList.remove('loading')
      
      if (this.elements.submitButton) {
        this.elements.submitButton.disabled = false
        this.elements.submitButton.classList.remove('loading')
        this.elements.submitButton.textContent = this.elements.submitButton.getAttribute('data-original-text') || 'Search'
      }
    }
  }

  /**
   * Get current state (useful for debugging and testing)
   */
  getState() {
    return { ...this.state }
  }

  /**
   * Client-side error logging and monitoring system
   * Captures error types, user actions, and system state while maintaining user privacy
   * @private
   */
  initializeErrorLogging() {
    // Initialize logging configuration
    this.logging = {
      enabled: true,
      level: this.options.logLevel || 'info', // debug, info, warn, error
      maxLogEntries: 100,
      sessionId: this.generateSessionId(),
      logs: [],
      errorCounts: {},
      userActions: [],
      performanceMetrics: {}
    }
    
    // Set up global error handlers for the component
    this.setupGlobalErrorHandlers()
    
    // Initialize performance monitoring
    this.initializePerformanceMonitoring()
    
    // Log initialization
    this.logInfo('PatientEducationFinder logging initialized', {
      sessionId: this.logging.sessionId,
      userAgent: this.sanitizeUserAgent(navigator.userAgent),
      timestamp: new Date().toISOString()
    })
  }

  /**
   * Generate a unique session ID for this component instance
   * @returns {string} Session ID
   * @private
   */
  generateSessionId() {
    return 'pef_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
  }

  /**
   * Sanitize user agent string to remove potentially sensitive information
   * @param {string} userAgent - Raw user agent string
   * @returns {string} Sanitized user agent
   * @private
   */
  sanitizeUserAgent(userAgent) {
    // Remove version numbers and specific build information
    return userAgent
      .replace(/\d+\.\d+\.\d+/g, 'X.X.X')
      .replace(/Build\/\w+/g, 'Build/XXX')
      .replace(/Version\/[\d.]+/g, 'Version/X.X')
  }

  /**
   * Set up global error handlers for the component
   * @private
   */
  setupGlobalErrorHandlers() {
    // Capture unhandled promise rejections within the component
    if (typeof window !== 'undefined') {
      // Override addEventListener to capture errors from our component
      this.componentErrorHandler = (event) => {
        if (event.target && this.container.contains(event.target)) {
          this.logError('Unhandled component error', {
            error: event.error?.message || 'Unknown error',
            stack: event.error?.stack?.split('\n').slice(0, 5).join('\n'), // Limit stack trace
            target: event.target.tagName,
            type: event.type
          })
        }
      }
      
      window.addEventListener('error', this.componentErrorHandler)
      window.addEventListener('unhandledrejection', this.componentErrorHandler)
    }
  }

  /**
   * Initialize performance monitoring
   * @private
   */
  initializePerformanceMonitoring() {
    this.logging.performanceMetrics = {
      initTime: performance.now(),
      dataLoadTime: null,
      searchTimes: [],
      errorRecoveryTimes: [],
      userInteractionTimes: []
    }
  }

  /**
   * Log an error with context and privacy protection
   * @param {string} message - Error message
   * @param {Object} context - Error context (will be sanitized)
   * @param {Error} error - Optional error object
   * @private
   */
  logError(message, context = {}, error = null) {
    if (!this.logging?.enabled) return
    
    const sanitizedContext = this.sanitizeLogContext(context)
    const errorEntry = {
      level: 'error',
      message,
      context: sanitizedContext,
      timestamp: new Date().toISOString(),
      sessionId: this.logging.sessionId,
      userAction: this.getLastUserAction(),
      componentState: this.getComponentStateForLogging(),
      errorId: this.generateErrorId(message, sanitizedContext)
    }
    
    // Add error stack if available (sanitized)
    if (error && error.stack) {
      errorEntry.stack = this.sanitizeStackTrace(error.stack)
    }
    
    // Track error frequency
    this.trackErrorFrequency(errorEntry.errorId)
    
    // Add to logs
    this.addLogEntry(errorEntry)
    
    // Console logging for development
    if (this.isDevelopmentMode()) {
      console.error(`[PatientEducationFinder] ${message}`, sanitizedContext, error)
    }
    
    // Send to monitoring service if configured
    this.sendToMonitoringService(errorEntry)
  }

  /**
   * Log a warning with context
   * @param {string} message - Warning message
   * @param {Object} context - Warning context
   * @private
   */
  logWarning(message, context = {}) {
    if (!this.logging?.enabled || this.logging.level === 'error') return
    
    const warningEntry = {
      level: 'warn',
      message,
      context: this.sanitizeLogContext(context),
      timestamp: new Date().toISOString(),
      sessionId: this.logging.sessionId,
      componentState: this.getComponentStateForLogging()
    }
    
    this.addLogEntry(warningEntry)
    
    if (this.isDevelopmentMode()) {
      console.warn(`[PatientEducationFinder] ${message}`, warningEntry.context)
    }
  }

  /**
   * Log an info message with context
   * @param {string} message - Info message
   * @param {Object} context - Info context
   * @private
   */
  logInfo(message, context = {}) {
    if (!this.logging?.enabled || ['error', 'warn'].includes(this.logging.level)) return
    
    const infoEntry = {
      level: 'info',
      message,
      context: this.sanitizeLogContext(context),
      timestamp: new Date().toISOString(),
      sessionId: this.logging.sessionId
    }
    
    this.addLogEntry(infoEntry)
    
    if (this.isDevelopmentMode() && this.logging.level === 'debug') {
      console.info(`[PatientEducationFinder] ${message}`, infoEntry.context)
    }
  }

  /**
   * Log a debug message with context
   * @param {string} message - Debug message
   * @param {Object} context - Debug context
   * @private
   */
  logDebug(message, context = {}) {
    if (!this.logging?.enabled || this.logging.level !== 'debug') return
    
    const debugEntry = {
      level: 'debug',
      message,
      context: this.sanitizeLogContext(context),
      timestamp: new Date().toISOString(),
      sessionId: this.logging.sessionId,
      componentState: this.getComponentStateForLogging()
    }
    
    this.addLogEntry(debugEntry)
    
    if (this.isDevelopmentMode()) {
      console.debug(`[PatientEducationFinder] ${message}`, debugEntry.context)
    }
  }

  /**
   * Log user actions for context in error reports
   * @param {string} action - Action name
   * @param {Object} details - Action details
   * @private
   */
  logUserAction(action, details = {}) {
    if (!this.logging?.enabled) return
    
    const actionEntry = {
      action,
      details: this.sanitizeLogContext(details),
      timestamp: new Date().toISOString(),
      sessionId: this.logging.sessionId
    }
    
    // Keep only last 20 user actions
    this.logging.userActions.push(actionEntry)
    if (this.logging.userActions.length > 20) {
      this.logging.userActions.shift()
    }
    
    this.logDebug('User action logged', { action, details: actionEntry.details })
  }

  /**
   * Log performance metrics
   * @param {string} metric - Metric name
   * @param {number} value - Metric value
   * @param {Object} context - Additional context
   * @private
   */
  logPerformance(metric, value, context = {}) {
    if (!this.logging?.performanceMetrics) return
    
    if (!this.logging.performanceMetrics[metric]) {
      this.logging.performanceMetrics[metric] = []
    }
    
    const performanceEntry = {
      value,
      context: this.sanitizeLogContext(context),
      timestamp: new Date().toISOString()
    }
    
    this.logging.performanceMetrics[metric].push(performanceEntry)
    
    // Keep only last 50 entries per metric
    if (this.logging.performanceMetrics[metric].length > 50) {
      this.logging.performanceMetrics[metric].shift()
    }
    
    this.logDebug('Performance metric logged', { metric, value, context })
  }

  /**
   * Sanitize log context to remove sensitive information
   * @param {Object} context - Raw context object
   * @returns {Object} Sanitized context
   * @private
   */
  sanitizeLogContext(context) {
    if (!context || typeof context !== 'object') return context
    
    const sanitized = {}
    const sensitiveKeys = ['password', 'token', 'key', 'secret', 'auth', 'session', 'cookie']
    
    for (const [key, value] of Object.entries(context)) {
      const lowerKey = key.toLowerCase()
      
      // Skip sensitive keys
      if (sensitiveKeys.some(sensitive => lowerKey.includes(sensitive))) {
        sanitized[key] = '[REDACTED]'
        continue
      }
      
      // Sanitize zipcode to remove specific location data
      if (lowerKey.includes('zipcode') || lowerKey.includes('zip')) {
        if (typeof value === 'string' && /^\d{5}$/.test(value)) {
          // Replace with region indicator (first 3 digits + XX)
          sanitized[key] = value.substring(0, 3) + 'XX'
        } else {
          sanitized[key] = value
        }
        continue
      }
      
      // Recursively sanitize nested objects
      if (typeof value === 'object' && value !== null) {
        sanitized[key] = this.sanitizeLogContext(value)
      } else {
        sanitized[key] = value
      }
    }
    
    return sanitized
  }

  /**
   * Sanitize stack trace to remove sensitive file paths
   * @param {string} stack - Raw stack trace
   * @returns {string} Sanitized stack trace
   * @private
   */
  sanitizeStackTrace(stack) {
    return stack
      .split('\n')
      .slice(0, 10) // Limit to 10 lines
      .map(line => {
        // Remove file paths, keep only function names and line numbers
        return line.replace(/https?:\/\/[^/]+/g, '[DOMAIN]')
          .replace(/file:\/\/[^:]+/g, '[FILE]')
          .replace(/\/[^/\s]+\//g, '/[PATH]/')
      })
      .join('\n')
  }

  /**
   * Generate a unique error ID for tracking
   * @param {string} message - Error message
   * @param {Object} context - Error context
   * @returns {string} Error ID
   * @private
   */
  generateErrorId(message, context) {
    const errorString = message + JSON.stringify(context)
    let hash = 0
    for (let i = 0; i < errorString.length; i++) {
      const char = errorString.charCodeAt(i)
      hash = ((hash << 5) - hash) + char
      hash = hash & hash // Convert to 32-bit integer
    }
    return 'err_' + Math.abs(hash).toString(36)
  }

  /**
   * Track error frequency for monitoring
   * @param {string} errorId - Error ID
   * @private
   */
  trackErrorFrequency(errorId) {
    if (!this.logging?.errorCounts) return
    
    if (!this.logging.errorCounts[errorId]) {
      this.logging.errorCounts[errorId] = {
        count: 0,
        firstSeen: new Date().toISOString(),
        lastSeen: new Date().toISOString()
      }
    }
    
    this.logging.errorCounts[errorId].count++
    this.logging.errorCounts[errorId].lastSeen = new Date().toISOString()
  }

  /**
   * Get the last user action for error context
   * @returns {Object|null} Last user action
   * @private
   */
  getLastUserAction() {
    return this.logging?.userActions?.length > 0 
      ? this.logging.userActions[this.logging.userActions.length - 1]
      : null
  }

  /**
   * Get component state for logging (sanitized)
   * @returns {Object} Component state
   * @private
   */
  getComponentStateForLogging() {
    return {
      isInitialized: this.state.isInitialized,
      hasData: !!this.state.data,
      currentZipcode: this.state.currentZipcode ? this.state.currentZipcode.substring(0, 3) + 'XX' : null,
      isLoading: this.state.isLoading,
      lastError: this.state.lastError ? {
        type: this.state.lastError.type,
        timestamp: this.state.lastError.timestamp,
        recoveryAttempts: this.state.lastError.recoveryAttempts
      } : null,
      containerClasses: Array.from(this.container.classList),
      dataManagerCount: this.state.data?.managers?.length || 0
    }
  }

  /**
   * Add log entry to the logs array
   * @param {Object} entry - Log entry
   * @private
   */
  addLogEntry(entry) {
    if (!this.logging?.logs) return
    
    this.logging.logs.push(entry)
    
    // Maintain max log entries
    if (this.logging.logs.length > this.logging.maxLogEntries) {
      this.logging.logs.shift()
    }
  }

  /**
   * Check if running in development mode
   * @returns {boolean} True if development mode
   * @private
   */
  isDevelopmentMode() {
    return (
      typeof window !== 'undefined' && 
      (window.location.hostname === 'localhost' || 
       window.location.hostname.includes('dev') ||
       window.location.hostname.includes('staging') ||
       this.options.debug === true)
    )
  }

  /**
   * Send error to monitoring service if configured
   * @param {Object} errorEntry - Error entry to send
   * @private
   */
  sendToMonitoringService(errorEntry) {
    // Only send in production and if monitoring endpoint is configured
    if (this.isDevelopmentMode() || !this.options.monitoringEndpoint) {
      return
    }
    
    try {
      // Send to monitoring service (non-blocking)
      fetch(this.options.monitoringEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          component: 'PatientEducationFinder',
          error: errorEntry,
          userAgent: this.sanitizeUserAgent(navigator.userAgent),
          url: window.location.pathname,
          timestamp: new Date().toISOString()
        })
      }).catch(monitoringError => {
        // Silently fail monitoring to avoid recursive errors
        console.warn('Failed to send error to monitoring service:', monitoringError)
      })
    } catch (error) {
      // Silently fail monitoring
      console.warn('Error sending to monitoring service:', error)
    }
  }

  /**
   * Get error summary for debugging
   * @returns {Object} Error summary
   */
  getErrorSummary() {
    if (!this.logging) return null
    
    const summary = {
      sessionId: this.logging.sessionId,
      totalLogs: this.logging.logs.length,
      errorCounts: Object.keys(this.logging.errorCounts).length,
      recentErrors: this.logging.logs
        .filter(log => log.level === 'error')
        .slice(-5)
        .map(log => ({
          message: log.message,
          timestamp: log.timestamp,
          errorId: log.errorId
        })),
      frequentErrors: Object.entries(this.logging.errorCounts)
        .sort(([,a], [,b]) => b.count - a.count)
        .slice(0, 5)
        .map(([errorId, data]) => ({
          errorId,
          count: data.count,
          firstSeen: data.firstSeen,
          lastSeen: data.lastSeen
        })),
      performanceMetrics: {
        initTime: this.logging.performanceMetrics.initTime,
        dataLoadTime: this.logging.performanceMetrics.dataLoadTime,
        averageSearchTime: this.calculateAverageSearchTime(),
        totalUserActions: this.logging.userActions.length
      }
    }
    
    return summary
  }

  /**
   * Calculate average search time from performance metrics
   * @returns {number|null} Average search time in milliseconds
   * @private
   */
  calculateAverageSearchTime() {
    const searchTimes = this.logging?.performanceMetrics?.searchTimes || []
    if (searchTimes.length === 0) return null
    
    const total = searchTimes.reduce((sum, entry) => sum + entry.value, 0)
    return Math.round(total / searchTimes.length)
  }

  /**
   * Export logs for debugging (development only)
   * @returns {Object|null} Logs object or null if not in development
   */
  exportLogs() {
    if (!this.isDevelopmentMode()) {
      console.warn('Log export is only available in development mode')
      return null
    }
    
    return {
      sessionId: this.logging.sessionId,
      logs: this.logging.logs,
      errorCounts: this.logging.errorCounts,
      userActions: this.logging.userActions,
      performanceMetrics: this.logging.performanceMetrics,
      summary: this.getErrorSummary()
    }
  }

  /**
   * Clear logs (development only)
   */
  clearLogs() {
    if (!this.isDevelopmentMode()) {
      console.warn('Log clearing is only available in development mode')
      return
    }
    
    this.logging.logs = []
    this.logging.errorCounts = {}
    this.logging.userActions = []
    this.logging.performanceMetrics = {
      initTime: performance.now(),
      dataLoadTime: null,
      searchTimes: [],
      errorRecoveryTimes: [],
      userInteractionTimes: []
    }
    
    console.log('PatientEducationFinder logs cleared')
  }

  /**
   * Cleanup method for proper memory management
   * Removes event listeners and clears references
   */
  destroy() {
    // Clear any pending debounce timer
    if (this.state.debounceTimer) {
      clearTimeout(this.state.debounceTimer)
      this.state.debounceTimer = null
    }

    // Remove event listeners
    if (this.elements.form) {
      this.elements.form.removeEventListener('submit', this.boundHandlers.handleSubmit)
    }
    
    if (this.elements.input) {
      this.elements.input.removeEventListener('input', this.boundHandlers.handleInput)
      this.elements.input.removeEventListener('change', this.boundHandlers.handleInputChange)
      this.elements.input.removeEventListener('paste', this.boundHandlers.handlePaste)
    }

    // Remove network event listeners
    if (this.boundHandlers.handleOnline) {
      window.removeEventListener('online', this.boundHandlers.handleOnline)
    }
    if (this.boundHandlers.handleOffline) {
      window.removeEventListener('offline', this.boundHandlers.handleOffline)
    }

    // Clean up error logging system
    if (this.componentErrorHandler) {
      window.removeEventListener('error', this.componentErrorHandler)
      window.removeEventListener('unhandledrejection', this.componentErrorHandler)
      this.componentErrorHandler = null
    }
    
    // Log destruction
    this.logInfo('PatientEducationFinder destroyed', {
      sessionDuration: this.logging?.performanceMetrics?.initTime ? 
        performance.now() - this.logging.performanceMetrics.initTime : null
    })
    
    // Clear logging data
    if (this.logging) {
      this.logging.logs = []
      this.logging.errorCounts = {}
      this.logging.userActions = []
      this.logging.performanceMetrics = {}
      this.logging = null
    }

    // Clear validation UI
    this.clearValidationUI()

    // Clear state
    this.state.isInitialized = false
    this.state.data = null
    this.state.lastSearchResult = null
    this.state.specificZipMap = null
    this.state.lastError = null
    this.state.retryCount = 0
    this.state.isRetrying = false

    // Clear DOM references
    Object.keys(this.elements).forEach(key => {
      this.elements[key] = null
    })

    console.log('PatientEducationFinder destroyed')
  }
}

export default PatientEducationFinder 